import {
  isSyncFresh,
  mergeCanonicalEntry,
  pendingCompletionEntries,
  remoteCompletionAction,
  settleWithin
} from "./packages/sync-core/src/index.js";
import {
  buildShareGallerySvg,
  SHARE_GALLERY_THEMES,
  shareGalleryFilename
} from "./share-gallery.js";
import {
  analyseRows,
  isPlayableGameDate,
  scoreForLength,
  SCORING_VERSION,
  totalScore as coreTotalScore
} from "./packages/game-core/src/index.js";
import { createGameEntrance } from "./game-entrance.js";
import { dailyShareSummary, formatDailyShareText, formatShareRow } from "./share-text.js";
import { createGameActivityTracker } from "./game-activity.js";

(function () {
  "use strict";

  const gameActivity = createGameActivityTracker({ storage: window.localStorage });
  void gameActivity.flush();
  window.addEventListener("online", () => { void gameActivity.flush(); });
  window.setInterval(() => { void gameActivity.flush(); }, 30000);

  const SLOT_COUNT = 5;
  const PUZZLE_SHAPE = [7, 5, 4, 4];
  const DIFFICULTIES = {
    easy: { label: "easy", mark: "松", preview: 4, moves: 3 },
    medium: { label: "medium", mark: "竹", preview: 2, moves: 2 },
    hard: { label: "hard", mark: "梅", preview: 0, moves: 1 }
  };
  const CLEAN_BOARD_BONUS = 15;
  const ROW_REVEAL_START_MS = 220;
  const ROW_REVEAL_GAP_MS = 500;
  const SCORE_COUNT_DURATION_MS = ROW_REVEAL_START_MS + ((SLOT_COUNT - 1) * ROW_REVEAL_GAP_MS) + 500;
  const POINTER_DRAG_THRESHOLD = 8;
  const TUTORIAL_DRAG_INITIAL_DELAY_MS = 7650;
  const TUTORIAL_DRAG_DURATION_MS = 1550;
  const TUTORIAL_DRAG_REPEAT_DELAY_MS = 1200;
  const DEFAULT_LOCALE = "en-GB";
  const DAILY_TIME_ZONE = "Europe/London";
  const SHARE_URL = "https://totonoeyo.com";
  const LOCALE_STORAGE_KEY = "wb.locale.v1";
  const LEGACY_HISTORY_STORAGE_PREFIX = "wb.history.v1.";
  const LEGACY_DRAFT_STORAGE_PREFIX = "wb.drafts.v1.";
  const HISTORY_STORAGE_PREFIX = "wb.history.v3.";
  const DRAFT_STORAGE_PREFIX = "wb.drafts.v3.";
  const SYNC_STORAGE_PREFIX = "wb.sync.v3.";
  const SYNC_CHANNEL_NAME = "totonoeyo-completion-sync-v1";
  const SYNC_UI_DEADLINE_MS = 2500;
  const API_REQUEST_TIMEOUT_MS = 8000;
  const SYNC_FRESH_MS = 30000;
  const PAST_MONTH_FRESH_MS = 5 * 60 * 1000;
  const VIEW_HISTORY_KEY = "totonoeyoView";
  const initialRoute = parseAppRoute(window.location.pathname);
  const useDocumentGameNavigation = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

  const dictionaryBundle = window.TOTONOEYO_DICTIONARIES || { locales: {} };
  let validWords = new Set();

  let selectedLocale = localStorage.getItem(LOCALE_STORAGE_KEY) || DEFAULT_LOCALE;
  let dailyManifest = null;
  let manifestStatus = "loading";
  let currentUser = null;
  let currentUserResolved = false;
  let selectedDifficulty = initialRoute.difficulty || "easy";
  let selectedDate = initialRoute.date || todayKey();
  let selectedInfoPage = initialRoute.page || "privacy";
  let calendarCursor = monthKey(selectedDate);
  let historyExpanded = false;
  let leaderboardExpanded = false;
  let leaderboardCalendarExpanded = false;
  let leaderboardState = {
    key: "",
    status: "idle",
    entries: [],
    currentUserEntry: null
  };
  let leaderboardRequest = null;
  let accountState = {
    key: "",
    status: "idle",
    data: null
  };
  let galleryState = {
    completionId: initialRoute.completionId || "",
    status: "idle",
    completion: null,
    theme: "plain",
    showTitle: true,
    showScore: true,
    error: ""
  };
  let siteBanner = null;
  let scrollResetToken = 0;
  let pointerDrag = null;
  let suppressTileClick = false;
  let pendingSwapMove = null;
  let gameMessageTimer = null;
  let tutorialLayoutFrame = null;
  let tutorialDismissTimer = null;
  let tutorialDragTimer = null;
  let tutorialDragTargetTimer = null;
  let tutorialDragAnimation = null;
  let tutorialDragGhost = null;
  let tutorialLastRow = -1;
  let syncGatePending = false;
  let initialSyncStarted = false;
  let initialSyncPromise = null;
  let pendingBackfillStarted = false;
  const syncRequests = new Map();
  const syncChannel = "BroadcastChannel" in window ? new BroadcastChannel(SYNC_CHANNEL_NAME) : null;
  let view = "home";
  let state = createEmptyGame(selectedDifficulty);

  const els = {
    appShell: document.getElementById("appShell"),
    home: document.getElementById("homeScreen"),
    account: document.getElementById("accountScreen"),
    game: document.getElementById("gameScreen"),
    dailyDateLabel: document.getElementById("dailyDateLabel"),
    localeSelect: document.getElementById("localeSelect"),
    todayCards: document.getElementById("todayCards"),
    dailyTotal: document.getElementById("dailyTotal"),
    dailyShare: document.getElementById("dailyShareButton"),
    dailyShareModal: document.getElementById("dailyShareModal"),
    closeDailyShare: document.getElementById("closeDailyShareButton"),
    dailyShareDate: document.getElementById("dailyShareDate"),
    dailyShareText: document.getElementById("dailyShareText"),
    dailyShareCopy: document.getElementById("dailyShareCopyButton"),
    leaderboardList: document.getElementById("leaderboardList"),
    leaderboardCard: document.getElementById("leaderboardCard"),
    leaderboardToggle: document.getElementById("leaderboardToggleButton"),
    leaderboardFullLink: document.getElementById("leaderboardFullLink"),
    leaderboard: document.getElementById("leaderboardScreen"),
    leaderboardBack: document.getElementById("leaderboardBackButton"),
    leaderboardCalendarToggle: document.getElementById("leaderboardCalendarToggleButton"),
    leaderboardCalendar: document.getElementById("leaderboardCalendar"),
    leaderboardMonthLabel: document.getElementById("leaderboardMonthLabel"),
    leaderboardPreviousMonth: document.getElementById("leaderboardPreviousMonthButton"),
    leaderboardNextMonth: document.getElementById("leaderboardNextMonthButton"),
    leaderboardPreviousDay: document.getElementById("leaderboardPreviousDayButton"),
    leaderboardNextDay: document.getElementById("leaderboardNextDayButton"),
    leaderboardDateLabel: document.getElementById("leaderboardDateLabel"),
    leaderboardPageStatus: document.getElementById("leaderboardPageStatus"),
    leaderboardTableWrap: document.getElementById("leaderboardTableWrap"),
    leaderboardTableBody: document.getElementById("leaderboardTableBody"),
    gallery: document.getElementById("galleryScreen"),
    info: document.getElementById("infoScreen"),
    infoBack: document.getElementById("infoBackButton"),
    infoTitle: document.getElementById("infoTitle"),
    infoPages: Array.from(document.querySelectorAll("[data-info-page]")),
    infoLinks: Array.from(document.querySelectorAll("[data-info-link]")),
    aboutHelp: document.getElementById("aboutHelpLink"),
    galleryBack: document.getElementById("galleryBackButton"),
    galleryPageStatus: document.getElementById("galleryPageStatus"),
    gallerySignIn: document.getElementById("gallerySignInButton"),
    galleryRetry: document.getElementById("galleryRetryButton"),
    galleryContent: document.getElementById("galleryContent"),
    galleryStyleCarousel: document.getElementById("galleryStyleCarousel"),
    galleryShowTitle: document.getElementById("galleryShowTitle"),
    galleryShowScore: document.getElementById("galleryShowScore"),
    galleryArtwork: document.getElementById("galleryArtwork"),
    galleryDownload: document.getElementById("galleryDownloadButton"),
    galleryDownloadStatus: document.getElementById("galleryDownloadStatus"),
    historyCalendar: document.getElementById("historyCalendar"),
    historyMonthLabel: document.getElementById("historyMonthLabel"),
    historyToggle: document.getElementById("historyToggleButton"),
    historyBody: document.getElementById("historyBody"),
    historySummary: document.getElementById("historySummary"),
    previousMonth: document.getElementById("previousMonthButton"),
    nextMonth: document.getElementById("nextMonthButton"),
    accountMenu: document.getElementById("accountMenuButton"),
    accountMenuName: document.getElementById("accountMenuName"),
    accountPageButton: document.getElementById("accountPageButton"),
    adminPageButton: document.getElementById("adminPageButton"),
    siteBanner: document.getElementById("siteBanner"),
    accountBack: document.getElementById("accountBackButton"),
    accountPageStatus: document.getElementById("accountPageStatus"),
    accountPageContent: document.getElementById("accountPageContent"),
    accountUsername: document.getElementById("accountUsername"),
    accountEmail: document.getElementById("accountEmail"),
    accountGamesPlayed: document.getElementById("accountGamesPlayed"),
    accountDaysPlayed: document.getElementById("accountDaysPlayed"),
    accountTotalScore: document.getElementById("accountTotalScore"),
    accountBestScore: document.getElementById("accountBestScore"),
    accountAverageScore: document.getElementById("accountAverageScore"),
    accountLastPlayed: document.getElementById("accountLastPlayed"),
    accountDifficultyStats: document.getElementById("accountDifficultyStats"),
    accountDetailUsername: document.getElementById("accountDetailUsername"),
    accountDetailEmail: document.getElementById("accountDetailEmail"),
    accountMemberSince: document.getElementById("accountMemberSince"),
    accountEmailVerified: document.getElementById("accountEmailVerified"),
    accountLanguage: document.getElementById("accountLanguage"),
    accountSettings: document.getElementById("accountSettingsButton"),
    accountSignOut: document.getElementById("accountSignOutButton"),
    productEmailSettings: document.getElementById("productEmailSettings"),
    productEmailsCheckbox: document.getElementById("productEmailsCheckbox"),
    productEmailsMessage: document.getElementById("productEmailsMessage"),
    board: document.getElementById("slotBoard"),
    selectedLetterStage: document.getElementById("selectedLetterStage"),
    head: document.getElementById("headLetter"),
    completionMessage: document.getElementById("completionMessage"),
    track: document.getElementById("letterTrack"),
    score: document.getElementById("scoreValue"),
    totalRow: document.querySelector(".total-row"),
    movesIndicator: document.getElementById("movesIndicator"),
    resultActions: document.getElementById("resultActions"),
    resultHome: document.getElementById("resultHomeButton"),
    difficultyButtons: Array.from(document.querySelectorAll("[data-difficulty]")),
    back: document.getElementById("backButton"),
    submit: document.getElementById("submitButton"),
    share: document.getElementById("shareButton"),
    shareLabel: document.getElementById("shareButtonLabel"),
    help: document.getElementById("helpButton"),
    homeHelp: document.getElementById("homeHelpButton"),
    helpModal: document.getElementById("helpModal"),
    closeHelp: document.getElementById("closeHelpButton"),
    settings: document.getElementById("settingsButton"),
    settingsModal: document.getElementById("settingsModal"),
    closeSettings: document.getElementById("closeSettingsButton"),
    publicNameSettings: document.getElementById("publicNameSettings"),
    publicNameValue: document.getElementById("publicNameValue"),
    rerollPublicName: document.getElementById("rerollPublicNameButton"),
    publicNameMessage: document.getElementById("publicNameMessage"),
    shareModal: document.getElementById("shareModal"),
    closeShare: document.getElementById("closeShareButton"),
    shareScore: document.getElementById("shareScore"),
    shareMoves: document.getElementById("shareMoves"),
    shareValidRows: document.getElementById("shareValidRows"),
    sharePoolWords: document.getElementById("sharePoolWords"),
    shareText: document.getElementById("shareText"),
    shareGallery: document.getElementById("shareGalleryButton"),
    copy: document.getElementById("copyButton"),
    signIn: document.getElementById("signInButton"),
    signOut: document.getElementById("signOutButton"),
    authModal: document.getElementById("authModal"),
    closeAuth: document.getElementById("closeAuthButton"),
    authForm: document.getElementById("authForm"),
    authEmail: document.getElementById("authEmail"),
    authSubmit: document.getElementById("authSubmitButton"),
    authCodeStep: document.getElementById("authCodeStep"),
    authCode: document.getElementById("authCode"),
    authEnterCode: document.getElementById("authEnterCodeButton"),
    authVerify: document.getElementById("authVerifyButton"),
    authChangeEmail: document.getElementById("authChangeEmailButton"),
    authMessage: document.getElementById("authMessage"),
    swapModal: document.getElementById("swapModal"),
    swapTitleText: document.getElementById("swapTitleText"),
    swapModalDots: document.getElementById("swapModalDots"),
    cancelSwap: document.getElementById("cancelSwapButton"),
    confirmSwap: document.getElementById("confirmSwapButton"),
    gameMessage: document.getElementById("gameMessage"),
    gameTutorial: document.getElementById("gameTutorial"),
    gameTutorialArrows: document.getElementById("gameTutorialArrows"),
    gameTutorialArrowPaths: document.getElementById("gameTutorialArrowPaths")
  };

  els.localeSelect.value = selectedLocale;
  els.localeSelect.addEventListener("cds-select-selected", (event) => {
    selectedLocale = event.detail?.value || els.localeSelect.value;
    localStorage.setItem(LOCALE_STORAGE_KEY, selectedLocale);
    if (currentUser) migrateLegacyStorageForCurrentUser();
    initialSyncStarted = false;
    initialSyncPromise = null;
    pendingBackfillStarted = false;
    leaderboardState = { key: "", status: "idle", entries: [], currentUserEntry: null };
    closeSettingsModal();
    loadDailyManifest();
    if (currentUser) void beginInitialCompletionSync();
  });

  els.back.addEventListener("click", showHome);
  els.homeHelp.addEventListener("click", openHelpModal);
  els.settings.addEventListener("click", openSettingsModal);
  els.closeSettings.addEventListener("click", closeSettingsModal);
  els.rerollPublicName.addEventListener("click", rerollPublicName);
  els.productEmailsCheckbox.addEventListener("change", updateProductEmailPreference);
  els.signIn.addEventListener("click", openAuthModal);
  els.signOut.addEventListener("click", signOut);
  els.accountPageButton.addEventListener("click", showAccount);
  els.adminPageButton.addEventListener("click", () => {
    if (currentUser?.isAdmin && currentUser.adminPath) window.location.assign(currentUser.adminPath);
  });
  els.accountBack.addEventListener("click", () => {
    showHome();
    scrollToTop();
  });
  els.accountSettings.addEventListener("click", openSettingsModal);
  els.accountSignOut.addEventListener("click", signOut);
  els.closeAuth.addEventListener("click", closeAuthModal);
  els.authForm.addEventListener("submit", (event) => {
    if (els.authCodeStep.hidden) void requestMagicLink(event);
    else void verifyLoginCode(event);
  });
  els.authSubmit.addEventListener("click", requestMagicLink);
  els.authVerify.addEventListener("click", verifyLoginCode);
  els.authCode.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.isComposing) void verifyLoginCode(event);
  });
  els.authEnterCode.addEventListener("click", () => showAuthCodeStep(true));
  els.authChangeEmail.addEventListener("click", () => showAuthCodeStep(false));
  els.historyToggle.addEventListener("cds-accordion-item-toggled", () => {
    historyExpanded = Boolean(els.historyToggle.open);
    render();
    if (historyExpanded) void syncCalendarMonth({ reason: "calendar-open" });
  });
  els.leaderboardToggle.addEventListener("click", () => {
    leaderboardExpanded = !leaderboardExpanded;
    els.leaderboardCard.classList.toggle("is-expanded", leaderboardExpanded);
    els.leaderboardToggle.setAttribute("aria-expanded", String(leaderboardExpanded));
  });
  els.leaderboardFullLink.addEventListener("click", (event) => {
    event.preventDefault();
    showLeaderboard(selectedDate);
  });
  els.leaderboardBack.addEventListener("click", showHome);
  els.leaderboardCalendarToggle.addEventListener("cds-accordion-item-toggled", () => {
    leaderboardCalendarExpanded = Boolean(els.leaderboardCalendarToggle.open);
    render();
    if (leaderboardCalendarExpanded) void syncCalendarMonth({ reason: "leaderboard-calendar-open" });
  });
  els.leaderboardPreviousMonth.addEventListener("click", () => {
    calendarCursor = shiftMonth(calendarCursor, -1);
    render();
    void syncCalendarMonth({ reason: "leaderboard-calendar-month" });
  });
  els.leaderboardNextMonth.addEventListener("click", () => {
    calendarCursor = shiftMonth(calendarCursor, 1);
    render();
    void syncCalendarMonth({ reason: "leaderboard-calendar-month" });
  });
  els.leaderboardPreviousDay.addEventListener("click", () => navigateLeaderboardDay(-1));
  els.leaderboardNextDay.addEventListener("click", () => navigateLeaderboardDay(1));
  els.galleryBack.addEventListener("click", leaveGallery);
  els.infoBack.addEventListener("click", leaveInfoPage);
  els.aboutHelp.addEventListener("click", (event) => {
    event.preventDefault();
    openHelpModal();
  });
  els.infoLinks.forEach((link) => link.addEventListener("click", (event) => {
    event.preventDefault();
    showInfoPage(link.dataset.infoLink);
  }));
  els.gallerySignIn.addEventListener("click", openAuthModal);
  els.galleryRetry.addEventListener("click", () => {
    galleryState.status = "idle";
    galleryState.error = "";
    void loadGalleryCompletion(galleryState.completionId);
  });
  els.galleryDownload.addEventListener("click", downloadGalleryPng);
  els.galleryShowTitle.addEventListener("change", updateGalleryDisplayOptions);
  els.galleryShowScore.addEventListener("change", updateGalleryDisplayOptions);
  els.previousMonth.addEventListener("click", () => {
    calendarCursor = shiftMonth(calendarCursor, -1);
    render();
    void syncCalendarMonth({ reason: "calendar-month" });
  });
  els.nextMonth.addEventListener("click", () => {
    calendarCursor = shiftMonth(calendarCursor, 1);
    render();
    void syncCalendarMonth({ reason: "calendar-month" });
  });

  els.submit.addEventListener("click", submitGame);
  els.resultHome.addEventListener("click", showHome);
  const gameEntrance = createGameEntrance({
    game: els.game, track: els.track, board: els.board, head: els.head,
    onFinish: () => renderGameTutorial()
  });
  els.game.addEventListener("pointerdown", () => gameEntrance.finish(), { capture: true });
  els.head.addEventListener("pointerdown", startPointerHeadDrag);
  els.cancelSwap.addEventListener("click", closeSwapModal);
  els.confirmSwap.addEventListener("click", confirmSwapMove);
  els.share.addEventListener("click", openShareModal);
  els.dailyShare.addEventListener("click", openDailyShareModal);
  els.closeDailyShare.addEventListener("click", () => setModalOpen(els.dailyShareModal, false));
  els.dailyShareCopy.addEventListener("click", () => copyShareText(els.dailyShareText, els.dailyShareCopy));
  els.help.addEventListener("click", openHelpModal);
  els.closeHelp.addEventListener("click", closeHelpModal);
  els.closeShare.addEventListener("click", closeShareModal);
  els.shareGallery.addEventListener("click", openShareGallery);

  els.copy.addEventListener("click", async () => {
    els.shareText.value = buildShareText();
    await copyShareText(els.shareText, els.copy);
  });

  async function copyShareText(textarea, button) {
    textarea.select();
    try {
      await navigator.clipboard.writeText(textarea.value);
      button.textContent = "Copied";
      window.setTimeout(() => {
        button.textContent = "Copy text";
      }, 1400);
    } catch (error) {
      document.execCommand("copy");
    }
  }
  document.addEventListener("keydown", handleKeyboard);
  window.addEventListener("popstate", handleViewHistoryChange);
  window.addEventListener("storage", handleCrossTabStorageChange);
  window.addEventListener("resize", scheduleGameTutorialLayout);
  window.addEventListener("resize", () => gameEntrance.render());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && view === "game" && !state.submitted && !isPlayableGameDate(state.date)) {
      render();
    }
    if (document.visibilityState === "visible" && currentUser) {
      void syncSelectedDate({ reason: "foreground" });
    }
  });
  window.setInterval(() => {
    if (view === "game" && !state.submitted && !isPlayableGameDate(state.date)) render();
  }, 30000);
  syncChannel?.addEventListener("message", handleSyncBroadcast);
  window.history.replaceState({
    ...(window.history.state || {}),
    [VIEW_HISTORY_KEY]: initialRoute.view,
    date: initialRoute.date || null,
    difficulty: initialRoute.difficulty || null,
    completionId: initialRoute.completionId || null,
    fromView: null
  }, "", routePath(initialRoute));

  async function loadDailyManifest() {
    manifestStatus = "loading";
    dailyManifest = null;
    render();

    try {
      dailyManifest = await loadManifestForDate(selectedDate);
      if (dailyManifest) activateDictionary(dailyManifest);
      manifestStatus = dailyManifest ? "ready" : "missing";
      render();
    } catch (error) {
      manifestStatus = error.status === 404 ? "missing" : "error";
      dailyManifest = null;
      render();
    }
  }

  async function loadManifestForDate(date) {
    const response = await fetch(`/puzzles/${selectedLocale}/${date}.json`, { cache: "no-store" });
    if (response.status === 404) return null;
    if (!response.ok) {
      const error = new Error(`Unable to load puzzle ${date}.`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  function activateDictionary(manifest) {
    if (dictionaryBundle.version !== manifest.dictionaryVersion) {
      throw new Error(`Dictionary ${manifest.dictionaryVersion} is not bundled.`);
    }
    prepareBundledDictionary(manifest.locale);
  }

  function prepareBundledDictionary(locale) {
    const localeWords = dictionaryBundle.locales?.[locale];
    if (!Array.isArray(localeWords)) throw new Error(`Dictionary locale ${locale} is not bundled.`);
    const commonWords = dictionaryBundle.schemaVersion === 2 ? dictionaryBundle.common : [];
    if (!Array.isArray(commonWords)) {
      throw new Error("Shared dictionary words are not bundled.");
    }
    validWords = new Set(commonWords);
    localeWords.forEach((word) => validWords.add(word));
    recalculateLocalHistoryScores();
  }

  function recalculateLocalHistoryScores() {
    const key = historyStorageKey();
    const history = readStoredObject(key);
    let changed = false;
    for (const dateEntries of Object.values(history)) {
      for (const entry of Object.values(dateEntries || {})) {
        if (entry.scoringVersion === SCORING_VERSION || !Array.isArray(entry.rows)) continue;
        const analyses = analyseRows(entry.rows, validWords, selectedLocale);
        entry.score = coreTotalScore(analyses, 20);
        entry.validRows = analyses.filter((row) => row.valid).length;
        entry.scoringVersion = SCORING_VERSION;
        changed = true;
      }
    }
    if (changed) localStorage.setItem(key, JSON.stringify(history));
  }

  async function loadCurrentUser() {
    try {
      const response = await fetchWithTimeout("/api/me", {
        credentials: "include"
      });
      if (!response.ok) return;
      const payload = await response.json();
      currentUser = payload.user || null;
      if (currentUser) {
        migrateLegacyStorageForCurrentUser();
        if (validWords.size > 0) recalculateLocalHistoryScores();
        leaderboardState = { key: "", status: "idle", entries: [], currentUserEntry: null };
        void beginInitialCompletionSync();
      }
    } catch (error) {
      currentUser = null;
    } finally {
      currentUserResolved = true;
      render();
      if (view === "gallery" && galleryState.completionId) {
        void loadGalleryCompletion(galleryState.completionId);
      }
    }
  }

  async function loadSiteBanner() {
    try {
      const response = await fetch("/api/banner", { credentials: "include" });
      if (!response.ok) return;
      const payload = await response.json();
      siteBanner = payload.banner || null;
    } catch (error) {
      siteBanner = null;
    }
  }

  function startGame(difficultyKey, historyMode = "push") {
    selectedDifficulty = difficultyKey in DIFFICULTIES ? difficultyKey : "easy";
    const gameDate = selectedDate;
    const saved = historyForLocale()[gameDate]?.[selectedDifficulty];
    if (saved) {
      state = createCompletedGame(saved);
      enterGameView(historyMode);
      return true;
    }

    if (!isPlayableGameDate(gameDate)) return false;

    const draft = draftsForLocale()[gameDate]?.[selectedDifficulty];
    if (draft && draft.puzzleVersion === dailyManifest?.puzzleVersion) {
      state = createDraftGame(draft);
      enterGameView(historyMode);
      return true;
    }

    if (!dailyManifest?.challenges?.[selectedDifficulty]) return false;
    state = createGame(selectedDifficulty);
    saveDraftGame();
    enterGameView(historyMode);
    return true;
  }

  async function openGame(difficultyKey, historyMode = "push") {
    if (currentUser) {
      await settleWithin(
        syncSelectedDate({ reason: "before-play" }),
        SYNC_UI_DEADLINE_MS
      );
    }
    return startGame(difficultyKey, historyMode);
  }

  function enterGameView(historyMode = "push") {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    scrollToTop();
    updateViewHistory("game", historyMode);
    closeShareModal();
    if (!state.submitted && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gameEntrance.start();
    } else {
      gameEntrance.finish(false);
    }
    const pendingPoolSeed = state.poolRevealPending ? state.seed : null;
    if (pendingPoolSeed && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      state.poolRevealPending = false;
    }
    render();
    if (state.poolRevealPending) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (view !== "game" || state.seed !== pendingPoolSeed || !state.poolRevealPending) return;
          state.poolRevealPending = false;
          render();
        });
      });
    }
    scrollToTop();
  }

  function showHome() {
    if (
      view !== "home"
      && window.history.state?.[VIEW_HISTORY_KEY] === view
      && window.history.state?.fromView === "home"
    ) {
      window.history.back();
      return;
    }
    updateViewHistory("home", "replace");
    enterHomeView();
  }

  function showLeaderboard(date, historyMode = "push") {
    selectedDate = date;
    calendarCursor = monthKey(date);
    updateViewHistory("leaderboard", historyMode);
    render();
    scrollToTop();
    loadLeaderboard(date);
  }

  function showGallery(completionId, historyMode = "push") {
    const isSameCompletion = galleryState.completionId === completionId;
    galleryState = {
      completionId,
      status: isSameCompletion ? galleryState.status : "idle",
      completion: isSameCompletion ? galleryState.completion : null,
      theme: isSameCompletion ? galleryState.theme : "plain",
      showTitle: isSameCompletion ? galleryState.showTitle : true,
      showScore: isSameCompletion ? galleryState.showScore : true,
      error: ""
    };
    updateViewHistory("gallery", historyMode);
    render();
    scrollToTop();
    void loadGalleryCompletion(completionId);
  }

  function showInfoPage(page, historyMode = "push") {
    selectedInfoPage = page;
    updateViewHistory("info", historyMode);
    render();
    scrollToTop();
  }

  function leaveInfoPage() {
    updateViewHistory("home", "replace");
    enterHomeView();
    scrollToTop();
  }

  function leaveGallery() {
    if (
      window.history.state?.[VIEW_HISTORY_KEY] === "gallery"
      && window.history.state?.fromView
    ) {
      window.history.back();
      return;
    }
    showHome();
  }

  function navigateLeaderboardDay(offset) {
    const nextDate = shiftDate(selectedDate, offset);
    if (nextDate > todayKey()) return;
    showLeaderboard(nextDate);
  }

  function enterHomeView() {
    view = "home";
    state.selectedTileId = null;
    state.draggingTileId = null;
    closeSwapModal();
    hideGameMessage();
    closeShareModal();
    closeHelpModal();
    render();
  }

  function updateViewHistory(nextView, historyMode = "push") {
    const nextRoute = nextView === "game"
      ? { view: nextView, date: state.date, difficulty: state.difficultyKey }
      : nextView === "leaderboard"
        ? { view: nextView, date: selectedDate }
        : nextView === "gallery"
          ? { view: nextView, completionId: galleryState.completionId }
        : nextView === "info"
          ? { view: nextView, page: selectedInfoPage }
      : { view: nextView };
    const nextPath = routePath(nextRoute);

    if (historyMode === "none") {
      view = nextView;
      return;
    }
    if (
      historyMode === "push"
      && view === nextView
      && window.location.pathname === nextPath
    ) return;

    const previousView = view;
    window.history[historyMode === "replace" ? "replaceState" : "pushState"]({
      ...(window.history.state || {}),
      [VIEW_HISTORY_KEY]: nextView,
      date: nextRoute.date || null,
      difficulty: nextRoute.difficulty || null,
      completionId: nextRoute.completionId || null,
      fromView: historyMode === "push" ? previousView : null
    }, "", nextPath);
    view = nextView;
  }

  async function handleViewHistoryChange() {
    const route = parseAppRoute(window.location.pathname);
    if (route.view === "game") {
      if (
        state.puzzleVersion
        && state.date === route.date
        && state.difficultyKey === route.difficulty
        && (state.submitted || isPlayableGameDate(state.date))
      ) {
        enterGameView("none");
        return;
      }

      selectedDate = route.date;
      selectedDifficulty = route.difficulty;
      calendarCursor = monthKey(route.date);
      await loadDailyManifest();
      if (window.location.pathname === routePath(route) && await openGame(route.difficulty, "none")) return;
      updateViewHistory("home", "replace");
      enterHomeView();
      return;
    }

    if (route.view === "account" && currentUser) {
      updateViewHistory("account", "none");
      render();
      loadAccountDetails();
      return;
    }

    if (route.view === "leaderboard") {
      selectedDate = route.date;
      calendarCursor = monthKey(route.date);
      showLeaderboard(route.date, "none");
      return;
    }

    if (route.view === "gallery") {
      showGallery(route.completionId, "none");
      return;
    }

    if (route.view === "info") {
      showInfoPage(route.page, "none");
      return;
    }

    if (route.view !== "home") updateViewHistory("home", "replace");
    enterHomeView();
  }

  function openCompletedGame(entry) {
    selectedDate = entry.date;
    selectedDifficulty = entry.difficulty;
    state = createCompletedGame(entry);
    enterGameView();
  }

  function createEmptyGame(difficultyKey) {
    return {
      seed: 0,
      date: selectedDate,
      locale: selectedLocale,
      puzzleVersion: "",
      difficultyKey: difficultyKey in DIFFICULTIES ? difficultyKey : "easy",
      poolWords: [],
      letters: [],
      cursor: 0,
      slots: Array.from({ length: SLOT_COUNT }, () => []),
      history: [],
      nextTileId: 1,
      selectedTileId: null,
      movesRemaining: DIFFICULTIES[difficultyKey]?.moves || DIFFICULTIES.easy.moves,
      submitted: false,
      scoreDisplay: 0,
      revealedRows: 0,
      scoring: false,
      poolRevealPending: false,
      activeTileId: null,
      draggingTileId: null,
      moveBurst: false,
      attemptId: null,
      remoteCompletion: null
    };
  }

  function createGame(difficultyKey) {
    const challenge = dailyManifest.challenges[difficultyKey];
    const seed = hashString(`${dailyManifest.locale}:${dailyManifest.date}:${difficultyKey}:${dailyManifest.puzzleVersion}`);
    const difficulty = DIFFICULTIES[difficultyKey] || DIFFICULTIES.easy;

    return {
      seed,
      date: dailyManifest.date,
      locale: dailyManifest.locale,
      puzzleVersion: dailyManifest.puzzleVersion,
      difficultyKey: difficultyKey in DIFFICULTIES ? difficultyKey : "easy",
      poolWords: (challenge.poolWords || challenge.targetWords || []).slice(),
      letters: challenge.letters.slice(),
      cursor: 0,
      slots: Array.from({ length: SLOT_COUNT }, () => []),
      history: [],
      nextTileId: 1,
      selectedTileId: null,
      movesRemaining: difficulty.moves,
      submitted: false,
      scoreDisplay: 0,
      revealedRows: 0,
      scoring: false,
      poolRevealPending: false,
      activeTileId: null,
      draggingTileId: null,
      moveBurst: false,
      attemptId: createAttemptId(),
      remoteCompletion: null
    };
  }

  function createCompletedGame(entry) {
    const game = createEmptyGame(entry.difficulty);
    const challenge = dailyManifest?.date === entry.date
      && dailyManifest?.locale === entry.locale
      && dailyManifest?.puzzleVersion === entry.puzzleVersion
      ? dailyManifest.challenges?.[entry.difficulty]
      : null;
    const poolWords = [entry.poolWords, entry.targetWords, challenge?.poolWords, challenge?.targetWords]
      .find((words) => Array.isArray(words) && words.length > 0) || [];
    const letters = entry.letters?.length ? entry.letters : challenge?.letters || [];
    game.seed = hashString(`${entry.locale}:${entry.date}:${entry.difficulty}:${entry.puzzleVersion}`);
    game.date = entry.date;
    game.locale = entry.locale;
    game.puzzleVersion = entry.puzzleVersion;
    game.poolWords = poolWords.slice();
    game.letters = letters.slice();
    game.cursor = letters.length;
    game.slots = entry.rows.map((row) => {
      return row.map((letter) => {
        const tile = {
          id: game.nextTileId,
          letter
        };
        game.nextTileId += 1;
        return tile;
      });
    });
    game.movesRemaining = entry.rowSwapsLeft;
    game.submitted = true;
    game.scoreDisplay = entry.score;
    game.revealedRows = SLOT_COUNT;
    game.poolRevealPending = game.poolWords.length > 0 && game.letters.length > 0;
    game.attemptId = entry.attemptId || null;
    return game;
  }

  function createDraftGame(entry) {
    const game = createEmptyGame(entry.difficulty);
    game.seed = entry.seed;
    game.date = entry.date;
    game.locale = entry.locale;
    game.puzzleVersion = entry.puzzleVersion;
    game.poolWords = (entry.poolWords || entry.targetWords || []).slice();
    game.letters = entry.letters.slice();
    game.cursor = entry.cursor;
    game.slots = entry.rows.map((row) => {
      return row.map((tileData) => ({
        id: tileData.id,
        letter: tileData.letter
      }));
    });
    game.nextTileId = entry.nextTileId;
    game.movesRemaining = entry.movesRemaining;
    game.attemptId = entry.attemptId || createAttemptId();
    return game;
  }

  function render() {
    if (view === "game" && !state.submitted && !isPlayableGameDate(state.date)) {
      canEditGame();
      return;
    }
    if (view !== "game" || state.submitted) gameEntrance.finish(false);
    const analyses = state.slots.map(analyseSlot);
    const total = totalScore(analyses);
    const isComplete = state.cursor >= state.letters.length;
    const isLocked = state.submitted;

    els.home.hidden = view !== "home";
    els.account.hidden = view !== "account";
    els.leaderboard.hidden = view !== "leaderboard";
    els.gallery.hidden = view !== "gallery";
    els.game.hidden = view !== "game";
    els.info.hidden = view !== "info";
    els.infoTitle.textContent = selectedInfoPage;
    els.infoPages.forEach((page) => { page.hidden = page.dataset.infoPage !== selectedInfoPage; });
    els.infoLinks.forEach((link) => {
      if (view === "info" && link.dataset.infoLink === selectedInfoPage) link.setAttribute("aria-current", "page");
      else link.removeAttribute("aria-current");
    });
    document.title = view === "info" ? `${selectedInfoPage} | totonoeyo` : "totonoeyo";
    els.game.classList.toggle("is-submitted", isLocked);
    els.game.classList.toggle("is-scoring", state.scoring);
    renderHome();
    renderAccount();
    renderLeaderboardPage();
    renderGallery();
    const displayLetter = selectedDisplayLetter();
    const placedTileSelected = Boolean(findTile(state.selectedTileId));
    setTileGlyph(els.head, displayLetter);
    const headCanDrag = !state.submitted && (placedTileSelected || state.cursor < state.letters.length);
    els.head.classList.toggle("is-unavailable", !headCanDrag);
    els.head.setAttribute("aria-hidden", String(isLocked));
    els.head.setAttribute("aria-label", headCanDrag ? `Drag letter ${displayLetter}` : "No letter available");
    els.selectedLetterStage.setAttribute("aria-label", isLocked ? "Game complete" : "Current letter");
    els.completionMessage.setAttribute("aria-hidden", String(!isLocked || state.scoring));
    els.score.textContent = String(state.submitted ? state.scoreDisplay : 0);
    els.score.classList.toggle("score-pop", state.scoring);
    const hasBonus = state.submitted && allPlacedLettersScore(analyses);
    els.totalRow.classList.toggle("is-final", isLocked && !state.scoring);
    els.movesIndicator.classList.toggle("moves-pop", state.moveBurst);
    els.submit.disabled = !isComplete || isLocked;
    els.submit.hidden = isLocked;
    els.shareLabel.textContent = currentUser ? "Share" : "Sign in to share";
    els.resultActions.hidden = !isLocked || state.scoring;
    els.share.disabled = !isLocked || state.scoring;
    if (!isLocked) closeShareModal();

    renderLetterTrack();
    renderMovesIndicator();
    renderSlots(analyses, isComplete, isLocked, hasBonus);
    gameEntrance.render();
    renderGameTutorial();

    if (state.submitted && state.scoreDisplay !== total && !state.scoring) {
      state.scoreDisplay = total;
      els.score.textContent = String(total);
    }
  }

  function renderGameTutorial() {
    const isVisible = view === "game"
      && !gameEntrance.active
      && state.difficultyKey === "easy"
      && state.cursor === 0
      && currentUserResolved
      && !currentUser
      && !state.submitted;
    els.gameTutorial.setAttribute("aria-hidden", String(!isVisible));
    if (isVisible) {
      const isEntering = !els.gameTutorial.classList.contains("is-visible");
      window.clearTimeout(tutorialDismissTimer);
      tutorialDismissTimer = null;
      els.gameTutorial.classList.remove("is-dismissing");
      tutorialItems().forEach((item) => item.style.removeProperty("opacity"));
      els.gameTutorial.classList.add("is-visible");
      scheduleGameTutorialLayout();
      if (isEntering) scheduleTutorialDragDemo(TUTORIAL_DRAG_INITIAL_DELAY_MS);
      return;
    }

    if (!els.gameTutorial.classList.contains("is-visible")) return;
    stopTutorialDragDemo();
    tutorialItems().forEach((item) => {
      item.style.opacity = getComputedStyle(item).opacity;
    });
    els.gameTutorial.classList.remove("is-visible");
    els.gameTutorial.classList.add("is-dismissing");
    window.clearTimeout(tutorialDismissTimer);
    tutorialDismissTimer = window.setTimeout(() => {
      els.gameTutorial.classList.remove("is-dismissing");
      tutorialItems().forEach((item) => item.style.removeProperty("opacity"));
      tutorialDismissTimer = null;
    }, 1600);
  }

  function tutorialItems() {
    return els.gameTutorial.querySelectorAll(
      ".game-tutorial-label, #gameTutorialArrowPaths > path"
    );
  }

  function scheduleTutorialDragDemo(delay) {
    window.clearTimeout(tutorialDragTimer);
    tutorialDragTimer = null;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    tutorialDragTimer = window.setTimeout(runTutorialDragDemo, delay);
  }

  function runTutorialDragDemo() {
    tutorialDragTimer = null;
    if (
      !els.gameTutorial.classList.contains("is-visible")
      || state.cursor !== 0
      || state.difficultyKey !== "easy"
    ) return;

    const shadows = Array.from(els.board.querySelectorAll(".row-shadow-tile"));
    if (shadows.length !== SLOT_COUNT) return;
    const choices = shadows
      .map((shadow, index) => ({ shadow, index }))
      .filter(({ index }) => index !== tutorialLastRow);
    const choice = choices[Math.floor(Math.random() * choices.length)];
    tutorialLastRow = choice.index;

    const overlayRect = els.gameTutorial.getBoundingClientRect();
    const headRect = els.head.getBoundingClientRect();
    const targetRect = choice.shadow.getBoundingClientRect();
    const ghost = els.head.cloneNode(true);
    ghost.removeAttribute("id");
    ghost.className = "letter-tile tutorial-drag-ghost";
    ghost.setAttribute("aria-hidden", "true");
    ghost.style.left = `${headRect.left - overlayRect.left}px`;
    ghost.style.top = `${headRect.top - overlayRect.top}px`;
    ghost.style.width = `${headRect.width}px`;
    ghost.style.height = `${headRect.height}px`;
    els.gameTutorial.appendChild(ghost);
    tutorialDragGhost = ghost;
    els.head.classList.add("is-tutorial-demo-source");

    const deltaX = targetRect.left + targetRect.width / 2 - (headRect.left + headRect.width / 2);
    const deltaY = targetRect.top + targetRect.height / 2 - (headRect.top + headRect.height / 2);
    const arcX = (choice.index % 2 === 0 ? -1 : 1) * (overlayRect.width <= 768 ? 18 : 30);
    tutorialDragAnimation = ghost.animate([
      { offset: 0, opacity: 0, transform: "translate(0, 0) scale(1)" },
      { offset: 0.12, opacity: 0.88, transform: "translate(0, -7px) scale(1.05)" },
      {
        offset: 0.52,
        opacity: 0.88,
        transform: `translate(${deltaX * 0.5 + arcX}px, ${deltaY * 0.44 - 12}px) scale(1.05)`
      },
      {
        offset: 0.86,
        opacity: 0.88,
        transform: `translate(${deltaX}px, ${deltaY}px) scale(1.02)`
      },
      { offset: 1, opacity: 0, transform: `translate(${deltaX}px, ${deltaY}px) scale(0.96)` }
    ], {
      duration: TUTORIAL_DRAG_DURATION_MS,
      easing: "cubic-bezier(0.35, 0, 0.2, 1)",
      fill: "both"
    });

    tutorialDragTargetTimer = window.setTimeout(() => {
      if (tutorialDragGhost === ghost) choice.shadow.classList.add("is-tutorial-target");
    }, TUTORIAL_DRAG_DURATION_MS * 0.55);

    tutorialDragAnimation.finished.then(() => {
      if (tutorialDragGhost !== ghost) return;
      finishTutorialDragDemo(ghost, choice.shadow);
      if (els.gameTutorial.classList.contains("is-visible") && state.cursor === 0) {
        scheduleTutorialDragDemo(TUTORIAL_DRAG_REPEAT_DELAY_MS);
      }
    }).catch(() => {});
  }

  function finishTutorialDragDemo(ghost, target) {
    window.clearTimeout(tutorialDragTargetTimer);
    tutorialDragTargetTimer = null;
    target?.classList.remove("is-tutorial-target");
    ghost?.remove();
    if (tutorialDragGhost === ghost) tutorialDragGhost = null;
    tutorialDragAnimation = null;
    els.head.classList.remove("is-tutorial-demo-source");
  }

  function stopTutorialDragDemo() {
    window.clearTimeout(tutorialDragTimer);
    window.clearTimeout(tutorialDragTargetTimer);
    tutorialDragTimer = null;
    tutorialDragTargetTimer = null;
    tutorialDragAnimation?.cancel();
    tutorialDragAnimation = null;
    tutorialDragGhost?.remove();
    tutorialDragGhost = null;
    els.head.classList.remove("is-tutorial-demo-source");
    els.board.querySelectorAll(".is-tutorial-target").forEach((target) => {
      target.classList.remove("is-tutorial-target");
    });
  }

  function scheduleGameTutorialLayout() {
    if (tutorialLayoutFrame !== null) cancelAnimationFrame(tutorialLayoutFrame);
    tutorialLayoutFrame = requestAnimationFrame(() => {
      tutorialLayoutFrame = null;
      layoutGameTutorial();
    });
  }

  function layoutGameTutorial() {
    if (!els.gameTutorial.classList.contains("is-visible") || view !== "game") return;
    const overlayRect = els.gameTutorial.getBoundingClientRect();
    const trackRect = els.track.getBoundingClientRect();
    const currentTrackTile = els.track.querySelector(".track-dot.is-current");
    const headRect = els.head.getBoundingClientRect();
    const boardRect = els.board.getBoundingClientRect();
    const moveCounter = els.movesIndicator.querySelector(".move-dots") || els.movesIndicator;
    const moveRect = moveCounter.getBoundingClientRect();
    const submitRect = els.submit.getBoundingClientRect();
    const rows = Array.from(els.board.querySelectorAll(".slot-row")).slice(0, SLOT_COUNT);
    const shadows = rows.map((row) => row.querySelector(".row-shadow-tile"));
    if (!overlayRect.width || rows.length < SLOT_COUNT || shadows.some((item) => !item)) return;

    const localRect = (rect) => ({
      left: rect.left - overlayRect.left,
      right: rect.right - overlayRect.left,
      top: rect.top - overlayRect.top,
      bottom: rect.bottom - overlayRect.top,
      width: rect.width,
      height: rect.height,
      x: rect.left - overlayRect.left + rect.width / 2,
      y: rect.top - overlayRect.top + rect.height / 2
    });
    const track = localRect(trackRect);
    const currentTile = localRect((currentTrackTile || els.track).getBoundingClientRect());
    const head = localRect(headRect);
    const board = localRect(boardRect);
    const moves = localRect(moveRect);
    const submit = localRect(submitRect);
    const rowBoxes = rows.map((row) => localRect(row.getBoundingClientRect()));
    const targets = shadows.map((shadow) => localRect(shadow.getBoundingClientRect()));
    const compact = overlayRect.width <= 768;
    const placeLabel = (key, left, top, maxWidth, align = "left") => {
      const label = els.gameTutorial.querySelector(`[data-tutorial-label="${key}"]`);
      label.style.left = "0";
      label.style.width = "max-content";
      label.style.maxWidth = `${maxWidth}px`;
      const width = label.getBoundingClientRect().width;
      const safeLeft = Math.max(0, Math.min(left, overlayRect.width - width));
      label.style.left = `${safeLeft}px`;
      label.style.top = `${Math.max(0, top)}px`;
      label.style.textAlign = align;
      return label;
    };

    const labels = compact
      ? {
          hidden: placeLabel("hidden", track.left + 8, track.bottom + 7, track.width * 0.29),
          place: placeLabel("place", head.right + 24, head.top - 4, Math.max(112, overlayRect.width - head.right - 16)),
          rows: placeLabel("rows", board.left + 28, rowBoxes[0].top + 8, board.width * 0.32),
          arrange: placeLabel("arrange", board.right - board.width * 0.34, rowBoxes[2].top + 7, board.width * 0.37),
          swaps: placeLabel("swaps", board.left + board.width * 0.17, rowBoxes[3].top + 7, board.width * 0.34),
          score: placeLabel("score", board.right - board.width * 0.34, rowBoxes[4].top + 8, board.width * 0.42)
        }
      : {
          hidden: placeLabel("hidden", track.left + track.width * 0.16, head.top + 4, track.width * 0.26),
          place: placeLabel("place", head.right + track.width * 0.08, head.top + 5, track.width * 0.32),
          rows: placeLabel("rows", board.left + board.width * 0.15, rowBoxes[1].top + 6, board.width * 0.3),
          arrange: placeLabel("arrange", board.right - board.width * 0.36, rowBoxes[2].top + 8, board.width * 0.27),
          swaps: placeLabel("swaps", board.left + board.width * 0.29, rowBoxes[3].top + 8, board.width * 0.29),
          score: placeLabel("score", board.right - board.width * 0.31, rowBoxes[4].top + 9, board.width * 0.27)
        };

    els.gameTutorialArrows.setAttribute("viewBox", `0 0 ${overlayRect.width} ${overlayRect.height}`);
    const labelRect = (label) => localRect(label.getBoundingClientRect());
    const nearestLabelPoint = (label, target) => {
      const rect = labelRect(label);
      const dx = target.x - rect.x;
      const dy = target.y - rect.y;
      if (Math.abs(dx) > Math.abs(dy)) {
        return { x: dx > 0 ? rect.right : rect.left, y: rect.y };
      }
      return { x: rect.x, y: dy > 0 ? rect.bottom : rect.top };
    };
    const setPath = (name, start, end, bend, step, exitStep = step) => {
      let path = els.gameTutorialArrowPaths.querySelector(`[data-tutorial-arrow="${name}"]`);
      if (!path) {
        path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.dataset.tutorialArrow = name;
        els.gameTutorialArrowPaths.appendChild(path);
      }
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const perpendicularX = -dy * bend;
      const perpendicularY = dx * bend;
      path.setAttribute(
        "d",
        `M ${start.x} ${start.y} C ${start.x + dx * 0.34 + perpendicularX} ${start.y + dy * 0.34 + perpendicularY}, ${start.x + dx * 0.68 + perpendicularX} ${start.y + dy * 0.68 + perpendicularY}, ${end.x} ${end.y}`
      );
      path.dataset.tutorialStep = String(step);
      path.dataset.tutorialExitStep = String(exitStep);
      path.setAttribute("marker-end", "url(#tutorialArrowhead)");
    };

    const trackTarget = { x: currentTile.x, y: currentTile.bottom + (compact ? 4 : 7) };
    const headTarget = { x: head.right, y: head.y };
    setPath("track", nearestLabelPoint(labels.hidden, trackTarget), trackTarget, -0.13, 1);
    setPath("place", nearestLabelPoint(labels.place, headTarget), headTarget, 0.12, 6, 1);
    setPath("row-one", nearestLabelPoint(labels.rows, targets[0]), targets[0], -0.12, 2);
    setPath("row-two", nearestLabelPoint(labels.rows, targets[1]), targets[1], 0.12, 2);

    const arrangeSpan = compact ? Math.min(42, board.width * 0.13) : Math.min(70, board.width * 0.09);
    const arrangeOffset = compact ? 10 : 18;
    setPath(
      "arrange-forward",
      { x: targets[2].x - arrangeOffset - arrangeSpan, y: targets[2].y - targets[2].height * 0.18 },
      { x: targets[2].x - arrangeOffset + arrangeSpan, y: targets[2].y - targets[2].height * 0.18 },
      -0.26,
      3
    );
    setPath(
      "arrange-back",
      { x: targets[2].x + arrangeOffset + arrangeSpan, y: targets[2].y + targets[2].height * 0.2 },
      { x: targets[2].x + arrangeOffset - arrangeSpan, y: targets[2].y + targets[2].height * 0.2 },
      -0.26,
      3
    );
    setPath(
      "row-swap",
      { x: targets[3].x, y: targets[3].y },
      { x: targets[4].x, y: targets[4].y },
      compact ? -0.52 : -0.68,
      4
    );
    const swapsLabel = labelRect(labels.swaps);
    setPath(
      "swap-counter",
      { x: swapsLabel.left, y: swapsLabel.y },
      { x: moves.x, y: moves.top - (compact ? 3 : 6) },
      0.12,
      4
    );
    setPath(
      "submit",
      nearestLabelPoint(labels.score, submit),
      { x: submit.x, y: submit.top - (compact ? 3 : 6) },
      -0.12,
      5
    );
  }

  function renderHome() {
    if (view !== "home") return;

    const date = selectedDate;
    const history = historyForLocale();
    const drafts = draftsForLocale();
    const todaysHistory = history[date] || {};
    const total = Object.values(todaysHistory).reduce((sum, entry) => sum + entry.score, 0);

    els.dailyDateLabel.textContent = manifestStatus === "ready"
      ? formatDisplayDate(date)
      : manifestStatus === "loading"
        ? "Loading selected puzzle"
        : "No published puzzle for this date";
    els.dailyTotal.textContent = String(total);
    els.dailyShare.hidden = !dailyShareSummary(currentUser, todaysHistory);
    els.leaderboardFullLink.href = `/leaderboard/${date}`;
    els.accountMenu.classList.toggle("is-authenticated", Boolean(currentUser));
    els.accountMenu.label = currentUser ? `Account: ${currentUser.displayName}` : "Account";
    els.accountMenu.index = currentUser ? 3 : 1;
    els.accountMenuName.textContent = currentUser?.displayName || "Player";
    els.accountMenuName.hidden = !currentUser;
    els.accountPageButton.hidden = !currentUser;
    els.adminPageButton.hidden = !currentUser?.isAdmin || !currentUser.adminPath;
    els.signIn.hidden = Boolean(currentUser);
    els.signOut.hidden = !currentUser;
    els.siteBanner.hidden = !siteBanner;
    els.siteBanner.textContent = siteBanner?.message || "";
    els.siteBanner.dataset.kind = siteBanner?.kind || "info";

    loadLeaderboard(date);
    renderLeaderboard();
    renderTodayCards(todaysHistory, drafts[date] || {}, date);
    renderHistoryPanel(history);
  }

  function showAccount() {
    if (!currentUser) {
      openAuthModal();
      return;
    }

    els.accountMenu.open = false;
    updateViewHistory("account");
    render();
    scrollToTop();
    loadAccountDetails();
  }

  async function loadAccountDetails() {
    if (!currentUser) return;
    const key = currentUser.id;
    if (accountState.key === key && accountState.status === "ready") return;

    accountState = { key, status: "loading", data: null };
    renderAccount();
    try {
      const response = await fetch("/api/account", { credentials: "include" });
      if (!response.ok) throw new Error("Could not load account details.");
      const data = await response.json();
      if (!currentUser || currentUser.id !== key) return;
      accountState = { key, status: "ready", data };
    } catch (error) {
      if (!currentUser || currentUser.id !== key) return;
      accountState = { key, status: "error", data: null };
    }
    renderAccount();
  }

  function renderAccount() {
    if (view !== "account") return;
    if (!currentUser) {
      view = "home";
      render();
      return;
    }

    const payload = accountState.data;
    const account = payload?.account || currentUser;
    const stats = payload?.stats;
    const loading = accountState.status === "loading" || accountState.status === "idle";

    els.accountPageStatus.hidden = accountState.status === "ready";
    els.accountPageStatus.textContent = loading
      ? "Loading account..."
      : "Account statistics are temporarily unavailable.";
    els.accountPageContent.hidden = false;
    els.accountPageContent.setAttribute("aria-busy", String(loading));

    els.accountUsername.textContent = account.publicName || account.displayName;
    els.accountEmail.textContent = account.email || "No email recorded";
    els.accountGamesPlayed.textContent = formatStat(stats?.gamesPlayed, loading);
    els.accountDaysPlayed.textContent = formatStat(stats?.daysPlayed, loading);
    els.accountTotalScore.textContent = formatStat(stats?.totalScore, loading);
    els.accountBestScore.textContent = formatStat(stats?.bestScore, loading);
    els.accountAverageScore.textContent = formatStat(stats?.averageScore, loading);
    els.accountLastPlayed.textContent = loading ? "—" : formatAccountDate(stats?.lastPlayed, "Never");

    els.accountDetailUsername.textContent = account.publicName || account.displayName;
    els.accountDetailEmail.textContent = account.email || "Not available";
    els.accountMemberSince.textContent = formatAccountDate(account.createdAt, "Not available");
    els.accountEmailVerified.textContent = formatAccountDate(account.emailVerified, account.email ? "Verified" : "Not available");
    els.accountLanguage.textContent = languageName(selectedLocale);
    renderAccountDifficultyStats(payload?.byDifficulty || [], loading);
  }

  function renderAccountDifficultyStats(rows, loading) {
    els.accountDifficultyStats.replaceChildren();
    const rowMap = new Map(rows.map((row) => [row.difficulty, row]));
    Object.entries(DIFFICULTIES).forEach(([difficultyKey, difficulty]) => {
      const stats = rowMap.get(difficultyKey);
      const gamesPlayed = Number(stats?.gamesPlayed || 0);
      const values = [
        difficulty.label,
        loading ? "—" : String(gamesPlayed),
        loading || !gamesPlayed ? (loading ? "—" : "0") : String(Math.round(Number(stats.totalScore || 0) / gamesPlayed)),
        loading ? "—" : String(Number(stats?.bestScore || 0))
      ];
      const tableRow = document.createElement("tr");
      values.forEach((value, index) => {
        const cell = document.createElement(index === 0 ? "th" : "td");
        if (index === 0) cell.scope = "row";
        if (index >= 2) cell.classList.add("score-number");
        cell.textContent = value;
        tableRow.appendChild(cell);
      });
      els.accountDifficultyStats.appendChild(tableRow);
    });
  }

  function formatStat(value, loading) {
    return loading ? "—" : Number(value || 0).toLocaleString("en-GB");
  }

  function formatAccountDate(value, fallback) {
    if (!value) return fallback;
    const source = String(value);
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(source) ? `${source}T00:00:00` : source.replace(" ", "T");
    const dateText = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized) ? normalized : `${normalized}Z`;
    const date = new Date(dateText);
    if (Number.isNaN(date.getTime())) return fallback;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  }

  function languageName(locale) {
    return locale === "en-US" ? "US English" : "British English";
  }

  function renderLeaderboard() {
    els.leaderboardList.replaceChildren();

    if (leaderboardState.status === "loading") {
      appendLeaderboardMessage("loading scores");
      return;
    }

    if (leaderboardState.status === "error") {
      appendLeaderboardMessage("scores unavailable");
      return;
    }

    if (!leaderboardState.entries.length) {
      appendLeaderboardMessage(currentUser ? "no scores yet" : "sign in to submit scores");
      return;
    }

    leaderboardState.entries.slice(0, 5).forEach((entry) => {
      const item = document.createElement("li");
      item.classList.toggle("is-current-user", Boolean(entry.isCurrentUser));

      const rank = document.createElement("span");
      rank.className = "leaderboard-rank";
      rank.textContent = `#${entry.rank}`;

      const name = document.createElement("span");
      name.className = "leaderboard-name";
      name.textContent = entry.displayName || "Player";

      const score = document.createElement("span");
      score.className = "leaderboard-score score-number";
      score.textContent = String(entry.total);

      item.append(rank, name, score);
      els.leaderboardList.appendChild(item);
    });
  }

  function renderLeaderboardPage() {
    if (view !== "leaderboard") return;

    els.leaderboardDateLabel.textContent = formatDisplayDate(selectedDate);
    els.leaderboardNextDay.disabled = selectedDate >= todayKey();
    els.leaderboardCalendarToggle.open = leaderboardCalendarExpanded;
    els.leaderboardCalendarToggle.title = "Calendar";
    if (leaderboardCalendarExpanded) {
      renderCalendarGrid({
        container: els.leaderboardCalendar,
        monthLabel: els.leaderboardMonthLabel,
        nextMonthButton: els.leaderboardNextMonth,
        history: historyForLocale(),
        onSelect: (dateKey) => showLeaderboard(dateKey),
        disableFuture: true
      });
    }
    els.leaderboardTableBody.replaceChildren();
    const isLoading = leaderboardState.status === "idle" || leaderboardState.status === "loading";
    const isError = leaderboardState.status === "error";
    const entries = leaderboardState.entries || [];

    els.leaderboardPageStatus.hidden = !isLoading && !isError && entries.length > 0;
    els.leaderboardPageStatus.textContent = isLoading
      ? "loading scores"
      : isError
        ? "scores are temporarily unavailable"
        : "no scores have been submitted for this day";
    els.leaderboardTableWrap.hidden = isLoading || isError || entries.length === 0;
    if (els.leaderboardTableWrap.hidden) return;

    entries.forEach((entry) => appendLeaderboardTableRow(entry));
    if (leaderboardState.currentUserEntry) {
      appendLeaderboardTableRow(leaderboardState.currentUserEntry, true);
    }
  }

  function appendLeaderboardTableRow(entry, outsideTop = false) {
    const row = document.createElement("tr");
    row.classList.toggle("is-current-user", Boolean(entry.isCurrentUser));
    row.classList.toggle("is-outside-top", outsideTop);

    const rank = document.createElement("td");
    rank.className = "leaderboard-table-rank score-number";
    rank.textContent = String(entry.rank);

    const name = document.createElement("th");
    name.scope = "row";
    name.textContent = entry.displayName || "Player";

    const scores = [entry.easy, entry.medium, entry.hard, entry.total].map((value) => {
      const cell = document.createElement("td");
      cell.className = "score-number";
      cell.textContent = value == null ? "—" : String(value);
      return cell;
    });

    row.append(rank, name, ...scores);
    els.leaderboardTableBody.appendChild(row);
  }

  function appendLeaderboardMessage(message) {
    const item = document.createElement("li");
    item.className = "leaderboard-empty";
    item.textContent = message;
    els.leaderboardList.appendChild(item);
  }

  function loadLeaderboard(date, { force = false, preserve = false } = {}) {
    const key = `${selectedLocale}:${date}:total`;
    const requestInFlight = leaderboardRequest?.key === key;
    if (requestInFlight) {
      return force
        ? leaderboardRequest.promise.then(() => loadLeaderboard(date, { force: true, preserve }))
        : leaderboardRequest.promise;
    }
    if (!force && leaderboardState.key === key && leaderboardState.status !== "idle") {
      return Promise.resolve();
    }

    const keepCurrentContent = preserve
      && leaderboardState.key === key
      && leaderboardState.status === "ready";
    leaderboardState = {
      key,
      status: keepCurrentContent ? "refreshing" : "loading",
      entries: keepCurrentContent ? leaderboardState.entries : [],
      currentUserEntry: keepCurrentContent ? leaderboardState.currentUserEntry : null
    };
    const promise = (async () => {
      try {
        const response = await fetch(`/api/leaderboards/${selectedLocale}/${date}/total`, { credentials: "include" });
        if (!response.ok) throw new Error(`Leaderboard request failed with status ${response.status}.`);
        const payload = await response.json();
        if (leaderboardState.key !== key) return;
        leaderboardState = {
          key,
          status: "ready",
          entries: payload.entries || [],
          currentUserEntry: payload.currentUserEntry || null
        };
      } catch (error) {
        if (leaderboardState.key !== key) return;
        leaderboardState = { key, status: "error", entries: [], currentUserEntry: null };
        console.warn("Leaderboard request failed.", error);
      } finally {
        if (leaderboardRequest?.promise === promise) leaderboardRequest = null;
        render();
      }
    })();
    leaderboardRequest = { key, promise };
    return promise;
  }

  async function loadGalleryCompletion(completionId) {
    if (view !== "gallery" || galleryState.completionId !== completionId) return;
    if (!currentUserResolved) {
      galleryState.status = "loading";
      render();
      return;
    }
    if (!currentUser) {
      galleryState.status = "signed-out";
      galleryState.completion = null;
      galleryState.error = "Sign in to open this share gallery.";
      render();
      return;
    }
    if (galleryState.status === "loading-request") return;

    galleryState.status = "loading-request";
    galleryState.error = "";
    render();
    try {
      const response = await fetchWithTimeout(`/api/completions/${encodeURIComponent(completionId)}`, {
        credentials: "include"
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(response.status === 404
          ? "This completed game could not be found."
          : payload.error || "The share gallery could not be loaded.");
      }
      if (view !== "gallery" || galleryState.completionId !== completionId) return;
      galleryState.status = "ready";
      galleryState.completion = payload.completion;
      galleryState.error = "";
    } catch (error) {
      if (view !== "gallery" || galleryState.completionId !== completionId) return;
      galleryState.status = "error";
      galleryState.completion = null;
      galleryState.error = error.name === "AbortError"
        ? "The share gallery server did not respond."
        : error.message || "The share gallery could not be loaded.";
    }
    render();
  }

  function renderGallery() {
    if (view !== "gallery") return;
    const ready = galleryState.status === "ready" && galleryState.completion;
    const signedOut = galleryState.status === "signed-out";
    const failed = galleryState.status === "error";

    els.galleryPageStatus.hidden = Boolean(ready);
    els.galleryPageStatus.textContent = ready
      ? ""
      : galleryState.error || (currentUserResolved ? "Loading completed game..." : "Checking your account...");
    els.gallerySignIn.hidden = !signedOut;
    els.galleryRetry.hidden = !failed;
    els.galleryContent.hidden = !ready;
    if (!ready) return;

    const newspaperTitleRequired = galleryState.theme === "newspaper";
    if (newspaperTitleRequired) galleryState.showTitle = true;
    els.galleryShowTitle.checked = galleryState.showTitle;
    els.galleryShowTitle.disabled = newspaperTitleRequired;
    els.galleryShowScore.checked = galleryState.showScore;
    const displayOptions = {
      showTitle: galleryState.showTitle,
      showScore: galleryState.showScore
    };

    els.galleryStyleCarousel.replaceChildren();
    SHARE_GALLERY_THEMES.forEach((theme) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "gallery-style-choice";
      button.dataset.theme = theme.id;
      button.setAttribute("role", "radio");
      button.setAttribute("aria-checked", String(galleryState.theme === theme.id));
      button.classList.toggle("is-selected", galleryState.theme === theme.id);

      const preview = document.createElement("span");
      preview.className = "gallery-style-preview";
      preview.setAttribute("aria-hidden", "true");
      preview.innerHTML = buildShareGallerySvg(galleryState.completion, theme.id, displayOptions);
      const label = document.createElement("span");
      label.className = "gallery-style-label";
      label.textContent = theme.label;
      button.append(preview, label);
      button.addEventListener("click", () => {
        galleryState.theme = theme.id;
        if (theme.id === "newspaper") galleryState.showTitle = true;
        els.galleryDownloadStatus.textContent = "";
        renderGallery();
      });
      els.galleryStyleCarousel.appendChild(button);
    });

    els.galleryArtwork.innerHTML = buildShareGallerySvg(
      galleryState.completion,
      galleryState.theme,
      displayOptions
    );
    els.galleryDownload.disabled = false;
  }

  function updateGalleryDisplayOptions() {
    galleryState.showTitle = galleryState.theme === "newspaper" || els.galleryShowTitle.checked;
    galleryState.showScore = els.galleryShowScore.checked;
    els.galleryDownloadStatus.textContent = "";
    renderGallery();
  }

  async function fetchWithTimeout(input, init = {}) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), API_REQUEST_TIMEOUT_MS);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  async function downloadGalleryPng() {
    const svg = els.galleryArtwork.querySelector("svg");
    if (!svg || !galleryState.completion) return;

    els.galleryDownload.disabled = true;
    els.galleryDownloadStatus.textContent = "Preparing 2400 x 3000 PNG...";
    try {
      await document.fonts?.ready;
      const exportSvg = svg.cloneNode(true);
      exportSvg.setAttribute("width", "2400");
      exportSvg.setAttribute("height", "3000");
      const markup = new XMLSerializer().serializeToString(exportSvg);
      const source = URL.createObjectURL(new Blob([markup], { type: "image/svg+xml;charset=utf-8" }));
      try {
        const image = new Image();
        image.src = source;
        await image.decode();
        const canvas = document.createElement("canvas");
        canvas.width = 2400;
        canvas.height = 3000;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("PNG export is not supported in this browser.");
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
        if (!blob) throw new Error("The PNG could not be created.");
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = shareGalleryFilename(galleryState.completion, galleryState.theme);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      } finally {
        URL.revokeObjectURL(source);
      }
      els.galleryDownloadStatus.textContent = "PNG downloaded.";
    } catch (error) {
      els.galleryDownloadStatus.textContent = error.message || "The PNG could not be created.";
    } finally {
      els.galleryDownload.disabled = false;
    }
  }

  function renderTodayCards(todaysHistory, todaysDrafts, date) {
    els.todayCards.replaceChildren();
    const isFutureDate = date > todayKey();
    const isPastDate = date < todayKey();

    Object.entries(DIFFICULTIES).forEach(([difficultyKey, difficulty]) => {
      const entry = todaysHistory[difficultyKey];
      const draft = todaysDrafts[difficultyKey];
      const challenge = dailyManifest?.challenges?.[difficultyKey];
      const canOpen = Boolean(entry || (!isPastDate && !isFutureDate && (draft || challenge)));
      const card = document.createElement("button");
      card.type = "button";
      card.className = "daily-card";
      card.disabled = syncGatePending || manifestStatus !== "ready" || !challenge || !canOpen;
      card.setAttribute("aria-busy", String(syncGatePending));
      card.dataset.difficulty = difficultyKey;
      card.addEventListener("click", () => {
        if (useDocumentGameNavigation) {
          window.location.assign(routePath({ view: "game", date, difficulty: difficultyKey }));
          return;
        }
        void openGame(difficultyKey);
      });

      const label = document.createElement("span");
      label.className = "daily-card-label";
      label.append(`${difficulty.label} `);
      const difficultyMark = document.createElement("span");
      difficultyMark.lang = "ja";
      difficultyMark.textContent = difficulty.mark;
      label.appendChild(difficultyMark);

      const cardState = document.createElement("div");
      cardState.className = "daily-card-state";

      const rows = entry?.rows || draft?.rows || [];
      const rowGraphic = createDailyCardRows(
        rows,
        entry ? "complete" : draft ? "draft" : "empty",
        entry ? analyseRows(rows, validWords, selectedLocale) : []
      );
      cardState.appendChild(rowGraphic);

      if (entry) {
        const score = document.createElement("strong");
        score.className = "daily-card-score score-number";
        score.textContent = String(entry.score);
        score.setAttribute("aria-label", `${entry.score} points`);
        cardState.appendChild(score);
      } else if (canOpen) {
        const action = document.createElement("span");
        action.className = "daily-card-action";
        if (draft) {
          action.append("resume");
        } else {
          action.append("play ");
          const playMark = document.createElement("span");
          playMark.className = "daily-card-action-japanese";
          playMark.lang = "ja";
          playMark.textContent = "はじめる";
          action.appendChild(playMark);
        }
        const arrow = document.createElement("span");
        arrow.className = "daily-card-action-arrow";
        arrow.setAttribute("aria-hidden", "true");
        arrow.textContent = "→";
        action.append(" ", arrow);
        cardState.appendChild(action);
      }

      const meta = document.createElement("span");
      meta.className = "daily-card-meta";
      if (entry) {
        meta.textContent = `${completedSummaryText(entry)}${entry.syncStatus === "rejected" ? " · local only" : ""}`;
      } else if (isPastDate) {
        meta.textContent = draft ? "unfinished · day ended" : "No local entry";
      } else if (draft) {
        meta.textContent = `${draft.cursor}/${draft.letters.length} ${pluralize(draft.cursor, "letter")} placed · ${draft.movesRemaining} ${pluralize(draft.movesRemaining, "swap")} left`;
      } else if (isFutureDate) {
        meta.textContent = "Not yet available";
      } else if (challenge) {
        meta.textContent = `${difficulty.moves} ${pluralize(difficulty.moves, "row swap")} · ${difficulty.preview} ${pluralize(difficulty.preview, "preview")}`;
      } else {
        meta.textContent = "Unavailable";
      }

      card.append(label, cardState, meta);
      els.todayCards.appendChild(card);
    });
  }

  function createDailyCardRows(rows, status, analyses) {
    const graphic = document.createElement("span");
    graphic.className = `daily-card-rows is-${status}`;
    graphic.setAttribute("role", "img");

    const populatedRows = Array.from({ length: SLOT_COUNT }, (_, index) => {
      return Array.isArray(rows[index]) ? rows[index].length : 0;
    });
    const rowsUsed = populatedRows.filter(Boolean).length;
    graphic.setAttribute(
      "aria-label",
      status === "complete"
        ? `${rowsUsed} of ${SLOT_COUNT} rows used`
        : status === "draft"
          ? `${rowsUsed} of ${SLOT_COUNT} rows in progress`
          : `${SLOT_COUNT} empty rows`
    );

    populatedRows.forEach((letterCount, index) => {
      const row = document.createElement("span");
      row.className = "daily-card-row";
      if (letterCount > 0) {
        row.classList.add("is-filled");
        if (status === "complete" && analyses[index]?.score <= 0) {
          row.classList.add("is-non-scoring");
        }
        row.style.setProperty("--row-fill", `${Math.min(100, 24 + letterCount * 9)}%`);
      }
      row.appendChild(document.createElement("span"));
      graphic.appendChild(row);
    });

    return graphic;
  }

  function completedSummaryText(entry) {
    const rowsUsed = Array.isArray(entry.rows)
      ? entry.rows.filter((row) => Array.isArray(row) && row.length > 0).length
      : 0;
    return `${rowsUsed} ${pluralize(rowsUsed, "row")} / ${entry.validRows} ${pluralize(entry.validRows, "word")} / ${entry.rowSwapsUsed} ${pluralize(entry.rowSwapsUsed, "swap")}`;
  }

  function pluralize(count, singular) {
    return Number(count) === 1 ? singular : `${singular}s`;
  }

  function renderHistoryPanel(history) {
    els.historyToggle.open = historyExpanded;
    els.historyToggle.title = "Calendar";
    renderHistorySummary(history);
    if (historyExpanded) {
      renderHistoryCalendar(history);
    }
  }

  function renderHistoryCalendar(history) {
    renderCalendarGrid({
      container: els.historyCalendar,
      monthLabel: els.historyMonthLabel,
      nextMonthButton: els.nextMonth,
      history,
      onSelect: selectDate
    });
  }

  function renderCalendarGrid({
    container,
    monthLabel,
    nextMonthButton,
    history,
    onSelect,
    disableFuture = false
  }) {
    container.replaceChildren();
    monthLabel.textContent = formatMonthLabel(calendarCursor);
    nextMonthButton.disabled = calendarCursor >= monthKey(todayKey());

    ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].forEach((weekday) => {
      const label = document.createElement("span");
      label.className = "history-weekday";
      label.textContent = weekday;
      container.appendChild(label);
    });

    const firstDate = new Date(`${calendarCursor}-01T00:00:00Z`);
    const year = firstDate.getUTCFullYear();
    const month = firstDate.getUTCMonth();
    const firstWeekday = (firstDate.getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

    for (let index = 0; index < firstWeekday; index += 1) {
      const empty = document.createElement("span");
      empty.className = "history-day is-empty";
      container.appendChild(empty);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateKey = `${calendarCursor}-${String(day).padStart(2, "0")}`;
      const entries = history[dateKey] || {};
      const completed = Object.values(entries);
      const score = completed.reduce((sum, entry) => sum + entry.score, 0);
      const cell = document.createElement("button");
      cell.type = "button";
      cell.className = "history-day";
      cell.classList.toggle("is-today", dateKey === todayKey());
      cell.classList.toggle("is-selected", dateKey === selectedDate);
      cell.dataset.intensity = String(Math.min(3, completed.length));
      cell.title = completed.length
        ? `${formatDisplayDate(dateKey)} · ${score} pts`
        : formatDisplayDate(dateKey);
      cell.setAttribute("aria-label", cell.title);
      cell.textContent = String(day);
      cell.disabled = disableFuture && dateKey > todayKey();
      cell.addEventListener("click", () => onSelect(dateKey));
      container.appendChild(cell);
    }
  }

  function renderHistorySummary(history) {
    const entries = Object.values(history[selectedDate] || {});
    if (!entries.length) {
      els.historySummary.textContent = "No completed games for this date";
      return;
    }

    const score = entries.reduce((sum, entry) => sum + entry.score, 0);
    const scoreValue = document.createElement("span");
    scoreValue.className = "score-number";
    scoreValue.textContent = String(score);
    els.historySummary.replaceChildren(`${entries.length}/3 games completed · `, scoreValue, " total");
  }

  function selectDate(dateKey) {
    selectedDate = dateKey;
    calendarCursor = monthKey(dateKey);
    leaderboardState = { key: "", status: "idle", entries: [], currentUserEntry: null };
    loadDailyManifest();
    void syncSelectedDate({ reason: "date-selection" });
  }

  function renderMovesIndicator() {
    const difficulty = DIFFICULTIES[state.difficultyKey];
    const totalMoves = difficulty.moves;
    const usedMoves = totalMoves - state.movesRemaining;
    els.movesIndicator.replaceChildren();

    const label = document.createElement("span");
    label.className = "moves-label";
    label.innerHTML = 'row swaps <span lang="ja">縦替え</span>';
    els.movesIndicator.appendChild(label);

    const dots = document.createElement("span");
    dots.className = "move-dots";
    renderMoveDots(dots, totalMoves, usedMoves);
    els.movesIndicator.appendChild(dots);
  }

  function renderMoveDots(container, totalMoves, usedMoves) {
    container.replaceChildren();
    for (let index = 0; index < totalMoves; index += 1) {
      const dot = document.createElement("span");
      dot.className = `move-dot ${index < usedMoves ? "is-used" : "is-left"}`;
      dot.setAttribute("aria-label", index < usedMoves ? "move used" : "move left");
      container.appendChild(dot);
    }
  }

  function renderLetterTrack() {
    const poolIsArranged = state.submitted
      && !state.scoring
      && !state.poolRevealPending
      && state.revealedRows >= SLOT_COUNT;
    const trackSeed = String(state.seed);
    if (
      poolIsArranged
      && els.track.dataset.mode === "pool"
      && els.track.dataset.seed === trackSeed
    ) return;

    const shouldAnimate = poolIsArranged
      && ["scoring", "pool-source"].includes(els.track.dataset.mode)
      && els.track.dataset.seed === trackSeed
      && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const previousPositions = shouldAnimate ? trackTilePositions() : new Map();
    const previousHeight = shouldAnimate ? els.track.getBoundingClientRect().height : 0;

    els.track.replaceChildren();
    els.track.classList.toggle("is-pool-arranged", poolIsArranged);
    els.track.dataset.mode = poolIsArranged
      ? "pool"
      : state.scoring
        ? "scoring"
        : state.poolRevealPending
          ? "pool-source"
          : "playing";
    els.track.dataset.seed = trackSeed;
    els.track.setAttribute("aria-label", poolIsArranged ? "Original pool words" : "Full letter track");

    if (poolIsArranged) {
      renderArrangedPoolWords();
      if (shouldAnimate) {
        els.track.style.minHeight = `${previousHeight}px`;
        animateTrackIntoPoolWords(previousPositions);
      }
      return;
    }

    els.track.style.removeProperty("min-height");
    const previewCount = DIFFICULTIES[state.difficultyKey].preview;
    const placedTileSelected = Boolean(findTile(state.selectedTileId));
    state.letters.forEach((letter, index) => {
      const item = document.createElement("span");
      item.className = "track-dot";
      item.dataset.sourceIndex = String(index);
      if (index < state.cursor) item.classList.add("is-played");
      if (index === state.cursor) {
        item.classList.add(placedTileSelected ? "is-deemphasized" : "is-current");
      }
      if (index > state.cursor && index <= state.cursor + previewCount) item.classList.add("is-preview");
      if (index <= state.cursor + previewCount) setTileGlyph(item, letter);
      item.setAttribute("aria-label", index < state.cursor
        ? `Played letter ${index + 1}: ${letter}`
        : index === state.cursor
          ? `${placedTileSelected ? "Available current" : "Current"} letter ${index + 1}: ${letter}`
          : index <= state.cursor + previewCount
            ? `Upcoming letter ${index + 1}: ${letter}`
            : `Hidden letter ${index + 1}`);
      els.track.appendChild(item);
    });
  }

  function renderArrangedPoolWords() {
    const sourceIndexes = new Map();
    state.letters.forEach((letter, index) => {
      const indexes = sourceIndexes.get(letter) || [];
      indexes.push(index);
      sourceIndexes.set(letter, indexes);
    });

    const desktopStarts = [];
    let desktopColumn = 1;
    state.poolWords.forEach((word) => {
      desktopStarts.push(desktopColumn);
      desktopColumn += word.length + 1;
    });
    const desktopColumns = Math.max(1, desktopColumn - 2);

    const mobileRows = [[0, 2], [1, 3]];
    const mobilePositions = new Map();
    const mobileColumns = Math.max(...mobileRows.map((wordIndexes) =>
      wordIndexes.reduce((sum, wordIndex) => sum + (state.poolWords[wordIndex]?.length || 0), 0)
        + Math.max(0, wordIndexes.length - 1)
    ));
    mobileRows.forEach((wordIndexes, rowIndex) => {
      const rowWidth = wordIndexes.reduce(
        (sum, wordIndex) => sum + (state.poolWords[wordIndex]?.length || 0),
        Math.max(0, wordIndexes.length - 1)
      );
      let column = Math.floor((mobileColumns - rowWidth) / 2) + 1;
      wordIndexes.forEach((wordIndex) => {
        mobilePositions.set(wordIndex, { column, row: rowIndex + 1 });
        column += (state.poolWords[wordIndex]?.length || 0) + 1;
      });
    });

    els.track.style.setProperty("--pool-columns", String(desktopColumns));
    els.track.style.setProperty("--pool-mobile-columns", String(mobileColumns));
    state.poolWords.forEach((word, wordIndex) => {
      const mobilePosition = mobilePositions.get(wordIndex) || { column: 1, row: 1 };
      [...word].forEach((letter, letterIndex) => {
        const sourceIndex = sourceIndexes.get(letter)?.shift();
        const item = document.createElement("span");
        item.className = "track-dot is-played is-pool-letter";
        item.dataset.sourceIndex = String(sourceIndex ?? `${wordIndex}-${letterIndex}`);
        item.style.setProperty("--pool-column", String(desktopStarts[wordIndex] + letterIndex));
        item.style.setProperty("--pool-row", "1");
        item.style.setProperty("--pool-mobile-column", String(mobilePosition.column + letterIndex));
        item.style.setProperty("--pool-mobile-row", String(mobilePosition.row));
        setTileGlyph(item, letter);
        item.setAttribute(
          "aria-label",
          `Pool word ${wordIndex + 1}, letter ${letterIndex + 1}: ${letter}`
        );
        els.track.appendChild(item);
      });
    });
  }

  function trackTilePositions() {
    return new Map(Array.from(els.track.children).map((item) => [
      item.dataset.sourceIndex,
      item.getBoundingClientRect()
    ]));
  }

  function animateTrackIntoPoolWords(previousPositions) {
    Array.from(els.track.children).forEach((item, index) => {
      const previous = previousPositions.get(item.dataset.sourceIndex);
      if (!previous || typeof item.animate !== "function") return;
      const next = item.getBoundingClientRect();
      const x = previous.left + previous.width / 2 - (next.left + next.width / 2);
      const y = previous.top + previous.height / 2 - (next.top + next.height / 2);
      const scale = previous.width / next.width;
      const arcDepth = Math.min(56, 26 + Math.abs(x) * 0.07);
      const middleScale = scale + (1 - scale) * 0.5;
      const settlingScale = scale + (1 - scale) * 0.82;
      item.animate([
        {
          offset: 0,
          transform: `translate(${x}px, ${y}px) scale(${scale})`
        },
        {
          offset: 0.5,
          transform: `translate(${x * 0.5}px, ${y * 0.5 + arcDepth}px) scale(${middleScale})`
        },
        {
          offset: 0.82,
          transform: `translate(${x * 0.18}px, ${y * 0.18 + arcDepth * 0.55}px) scale(${settlingScale})`
        },
        {
          offset: 1,
          transform: "translate(0, 0) scale(1)"
        }
      ], {
        duration: 1350,
        delay: index * 32,
        easing: "cubic-bezier(0.3, 0, 0.2, 1)",
        fill: "backwards"
      });
    });
  }

  function selectedDisplayLetter() {
    const selectedLocation = findTile(state.selectedTileId);
    if (selectedLocation) {
      return state.slots[selectedLocation.slotIndex][selectedLocation.tileIndex].letter;
    }
    return state.letters[state.cursor] || "-";
  }

  function renderSlots(analyses, isComplete, isLocked, hasBonus) {
    els.board.replaceChildren();

    if (isLocked) {
      const heading = document.createElement("div");
      heading.className = "board-score-heading";
      const label = document.createElement("span");
      label.innerHTML = 'score <span lang="ja">得点</span>';
      heading.appendChild(label);
      els.board.appendChild(heading);
    }

    analyses.forEach((analysis, index) => {
      const rowRevealed = state.submitted && index < state.revealedRows;
      const row = document.createElement("article");
      row.className = "slot-row";
      row.classList.toggle("is-playing", !state.submitted);
      row.classList.toggle("is-settling", analysis.tiles.some((tile) => tile.id === state.activeTileId));
      row.setAttribute("aria-label", `Row ${index + 1}`);
      if (rowRevealed) {
        row.classList.add("is-revealed");
      }
      if (rowRevealed && analysis.tiles.length > 0) {
        row.classList.add(analysis.valid ? "is-valid" : "is-invalid");
      }
      row.style.setProperty("--row-score-delay", `${analysis.tiles.length * 70}ms`);

      const label = document.createElement("div");
      label.className = "slot-label";
      label.textContent = String(index + 1);
      label.setAttribute("aria-hidden", "true");

      const word = document.createElement("div");
      word.className = "slot-word";
      word.dataset.slotIndex = String(index);
      const display = document.createElement("div");
      display.className = "word-display";
      display.dataset.slotIndex = String(index);
      display.appendChild(createInsertTarget(index, 0, isLocked, analysis.tiles.length === 0));
      analysis.tiles.forEach((tileData, tileIndex) => {
        const tile = document.createElement("button");
        tile.className = "slot-letter";
        tile.type = "button";
        setTileGlyph(tile, tileData.letter);
        tile.disabled = isLocked;
        tile.dataset.tileId = String(tileData.id);
        tile.dataset.slotIndex = String(index);
        tile.dataset.tileIndex = String(tileIndex);
        tile.style.setProperty("--wave-index", String(tileIndex));
        tile.classList.toggle("is-selected", tileData.id === state.selectedTileId);
        tile.classList.toggle("is-landing", tileData.id === state.activeTileId);
        tile.classList.toggle("is-dragging", tileData.id === state.draggingTileId);
        tile.classList.toggle("is-settled", rowRevealed);
        tile.title = "Select letter";
        tile.setAttribute("aria-label", `Select ${tileData.letter} in row ${index + 1}`);
        tile.draggable = !isLocked;
        tile.addEventListener("dragstart", (event) => {
          selectDragSource(tileData.id, tile);
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", String(tileData.id));
          setTileDragImage(event, tile);
        });
        tile.addEventListener("dragend", () => {
          state.draggingTileId = null;
          clearAllDropZones();
          render();
        });
        tile.addEventListener("dragover", (event) => {
          if (isLocked) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = "move";
          markInsertDropZone(display, dropPositionFromTile(event, tile, tileIndex));
        });
        tile.addEventListener("dragleave", () => {
          clearDisplayDropZones(display);
        });
        tile.addEventListener("drop", (event) => {
          if (isLocked) return;
          event.preventDefault();
          event.stopPropagation();
          const tileId = Number(event.dataTransfer.getData("text/plain"));
          if (!tileId) return;
          state.selectedTileId = tileId;
          clearDisplayDropZones(display);
          moveSelectedTo(index, dropPositionFromTile(event, tile, tileIndex));
        });
        tile.addEventListener("pointerdown", (event) => startPointerTileDrag(event, tileData.id, tile, isLocked));
        tile.addEventListener("click", () => {
          if (suppressTileClick) return;
          selectTile(tileData.id);
        });
        display.appendChild(tile);
        display.appendChild(createInsertTarget(index, tileIndex + 1, isLocked, false));
      });
      if (analysis.tiles.length === 0) {
        display.classList.add("is-empty");
        if (!isLocked) {
          const shadowTile = document.createElement("span");
          shadowTile.className = "row-shadow-tile";
          shadowTile.setAttribute("aria-hidden", "true");
          display.appendChild(shadowTile);
        }
      }
      word.addEventListener("click", (event) => {
        if (event.target === word || event.target === display) {
          activateInsertTarget(index, dropPositionFor(display, event.clientX));
        }
      });
      display.addEventListener("dragover", (event) => {
        if (isLocked) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "move";
        markDisplayDropZone(display, event.clientX);
      });
      display.addEventListener("dragleave", (event) => {
        if (!display.contains(event.relatedTarget)) {
          clearDisplayDropZones(display);
        }
      });
      display.addEventListener("drop", (event) => {
        if (isLocked) return;
        event.preventDefault();
        event.stopPropagation();
        const tileId = Number(event.dataTransfer.getData("text/plain"));
        if (!tileId) return;
        state.selectedTileId = tileId;
        clearDisplayDropZones(display);
        moveSelectedTo(index, dropPositionFor(display, event.clientX));
      });
      word.addEventListener("dragover", (event) => {
        if (isLocked) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      });
      word.addEventListener("drop", (event) => {
        if (isLocked) return;
        event.preventDefault();
        const tileId = Number(event.dataTransfer.getData("text/plain"));
        if (!tileId) return;
        state.selectedTileId = tileId;
        clearDisplayDropZones(display);
        moveSelectedTo(index, dropPositionFor(display, event.clientX));
      });
      word.append(display);

      const score = document.createElement("div");
      score.className = "slot-score score-number";
      score.textContent = state.submitted && index < state.revealedRows
        ? String(analysis.score)
        : "—";

      row.append(label, word, score);
      els.board.appendChild(row);
    });

    if (hasBonus && state.revealedRows >= SLOT_COUNT) {
      const row = document.createElement("article");
      row.className = "slot-row bonus-row";
      row.setAttribute("aria-label", `Soroi all letters bonus: ${CLEAN_BOARD_BONUS} points`);

      const label = document.createElement("div");
      label.className = "slot-word bonus-word";
      label.innerHTML = '<span class="bonus-label-content"><span lang="ja">揃い</span> soroi bonus</span>';

      const score = document.createElement("div");
      score.className = "slot-score score-number";
      score.textContent = String(CLEAN_BOARD_BONUS);

      row.append(label, score);
      els.board.appendChild(row);
    }
  }

  function renderPoolWordItems(container, poolWords) {
    container.replaceChildren();
    poolWords.forEach((word) => {
      const wordGroup = document.createElement("span");
      wordGroup.className = "share-pool-word";
      wordGroup.setAttribute("aria-label", word);

      Array.from(word).forEach((letter) => {
        const tile = document.createElement("span");
        tile.className = "share-pool-letter";
        tile.setAttribute("aria-hidden", "true");
        tile.textContent = letter;
        wordGroup.appendChild(tile);
      });

      container.appendChild(wordGroup);
    });
  }

  function createInsertTarget(slotIndex, position, isLocked, startsWord) {
    const target = document.createElement("button");
    target.className = "insert-target";
    target.type = "button";
    target.dataset.slotIndex = String(slotIndex);
    target.dataset.position = String(position);
    target.disabled = isLocked;
    const action = state.selectedTileId
      ? "Move selected letter"
      : startsWord
        ? "Start word"
        : "Place current letter";
    target.title = `${action} here`;
    target.setAttribute("aria-label", `${action} at position ${position + 1} in row ${slotIndex + 1}`);
    target.addEventListener("click", () => activateInsertTarget(slotIndex, position));
    target.addEventListener("dragover", (event) => {
      if (isLocked) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      target.classList.add("is-drop-target");
    });
    target.addEventListener("dragleave", () => {
      target.classList.remove("is-drop-target");
    });
    target.addEventListener("drop", (event) => {
      if (isLocked) return;
      event.preventDefault();
      event.stopPropagation();
      const tileId = Number(event.dataTransfer.getData("text/plain"));
      if (!tileId) return;
      state.selectedTileId = tileId;
      target.classList.remove("is-drop-target");
      moveSelectedTo(slotIndex, position);
    });
    return target;
  }

  function startPointerTileDrag(event, tileId, tile, isLocked) {
    if (isLocked || state.submitted || event.pointerType === "mouse" || !event.isPrimary) return;
    startPointerLetterDrag(event, {
      sourceType: "tile",
      tileId,
      letter: tile.textContent,
      sourceTile: tile
    });
  }

  function startPointerHeadDrag(event) {
    const selectedLocation = findTile(state.selectedTileId);
    if (state.submitted || (!selectedLocation && state.cursor >= state.letters.length) || !event.isPrimary) return;
    const selectedTile = selectedLocation
      ? state.slots[selectedLocation.slotIndex][selectedLocation.tileIndex]
      : null;
    startPointerLetterDrag(event, {
      sourceType: selectedTile ? "tile" : "head",
      tileId: selectedTile?.id || null,
      letter: selectedTile?.letter || state.letters[state.cursor],
      sourceTile: els.head
    });
  }

  function startPointerLetterDrag(event, source) {
    if (!canEditGame()) return;
    if (pointerDrag) return;
    pointerDrag = {
      pointerId: event.pointerId,
      sourceType: source.sourceType,
      tileId: source.tileId,
      letter: source.letter,
      startX: event.clientX,
      startY: event.clientY,
      didDrag: false,
      sourceTile: source.sourceTile,
      ghost: null
    };
    source.sourceTile.setPointerCapture?.(event.pointerId);
    document.addEventListener("pointermove", handlePointerTileDrag);
    document.addEventListener("pointerup", finishPointerTileDrag);
    document.addEventListener("pointercancel", cancelPointerTileDrag);
  }

  function handlePointerTileDrag(event) {
    if (!pointerDrag || event.pointerId !== pointerDrag.pointerId) return;

    const distance = Math.hypot(event.clientX - pointerDrag.startX, event.clientY - pointerDrag.startY);
    if (!pointerDrag.didDrag && distance < POINTER_DRAG_THRESHOLD) return;

    event.preventDefault();
    if (!pointerDrag.didDrag) {
      pointerDrag.didDrag = true;
      if (pointerDrag.sourceType === "tile") {
        selectDragSource(pointerDrag.tileId, pointerDrag.sourceTile);
      } else {
        clearSelectedTileStyles();
        state.selectedTileId = null;
        state.draggingTileId = null;
      }
      pointerDrag.sourceTile.classList.add("is-dragging");
      pointerDrag.ghost = createPointerDragGhost(pointerDrag);
    }

    positionPointerDragGhost(pointerDrag.ghost, event.clientX, event.clientY);
    clearAllDropZones();
    markPointerDropTarget(event.clientX, event.clientY);
  }

  function finishPointerTileDrag(event) {
    if (!pointerDrag || event.pointerId !== pointerDrag.pointerId) return;
    const drag = pointerDrag;
    cleanupPointerDragListeners();

    if (!drag.didDrag) {
      pointerDrag = null;
      return;
    }

    event.preventDefault();
    suppressTileClick = true;
    window.setTimeout(() => {
      suppressTileClick = false;
    }, 0);

    const target = pointerDropTarget(event.clientX, event.clientY);
    pointerDrag = null;
    clearPointerDragVisuals(drag);
    clearAllDropZones();
    if (target) {
      if (drag.sourceType === "head") {
        state.selectedTileId = null;
        placeLetter(target.slotIndex, target.position);
      } else {
        state.selectedTileId = drag.tileId;
        moveSelectedTo(target.slotIndex, target.position);
      }
      return;
    }

    state.draggingTileId = null;
    render();
  }

  function cancelPointerTileDrag(event) {
    if (!pointerDrag || event.pointerId !== pointerDrag.pointerId) return;
    cleanupPointerDragListeners();
    const drag = pointerDrag;
    pointerDrag = null;
    clearPointerDragVisuals(drag);
    state.draggingTileId = null;
    clearAllDropZones();
    render();
  }

  function cleanupPointerDragListeners() {
    document.removeEventListener("pointermove", handlePointerTileDrag);
    document.removeEventListener("pointerup", finishPointerTileDrag);
    document.removeEventListener("pointercancel", cancelPointerTileDrag);
  }

  function createPointerDragGhost(drag) {
    const ghost = document.createElement("span");
    const sourceRect = drag.sourceTile.getBoundingClientRect();
    ghost.className = "letter-tile pointer-drag-ghost";
    setTileGlyph(ghost, drag.letter);
    ghost.style.width = `${Math.max(38, sourceRect.width)}px`;
    document.body.appendChild(ghost);
    return ghost;
  }

  function setTileGlyph(element, letter) {
    const glyph = document.createElement("span");
    glyph.className = "tile-glyph";
    glyph.textContent = letter;
    element.replaceChildren(glyph);
  }

  function positionPointerDragGhost(ghost, x, y) {
    if (!ghost) return;
    ghost.style.left = `${x}px`;
    ghost.style.top = `${y}px`;
  }

  function clearPointerDragVisuals(drag) {
    if (!drag) return;
    drag.sourceTile.classList.remove("is-dragging");
    if (drag.sourceTile === els.head) drag.sourceTile.classList.remove("is-selected");
    drag.ghost?.remove();
  }

  function selectDragSource(tileId, sourceTile) {
    clearSelectedTileStyles();
    state.selectedTileId = tileId;
    state.draggingTileId = tileId;
    sourceTile.classList.add("is-selected", "is-dragging");
  }

  function clearSelectedTileStyles() {
    els.board.querySelectorAll(".slot-letter.is-selected").forEach((tile) => {
      tile.classList.remove("is-selected");
    });
    els.head.classList.remove("is-selected");
  }

  function markPointerDropTarget(x, y) {
    const target = pointerDropTarget(x, y);
    if (!target) return;
    const display = target.display || target.element.closest(".word-display");
    markInsertDropZone(display, target.position);
  }

  function pointerDropTarget(x, y) {
    const element = document.elementFromPoint(x, y)?.closest(".insert-target, .slot-letter, .word-display, .slot-word");
    if (!element || !els.board.contains(element)) return null;

    if (element.classList.contains("insert-target")) {
      if (element.disabled) return null;
      return pointerTargetFromPosition(element, Number(element.dataset.slotIndex), Number(element.dataset.position), "insert");
    }

    if (element.classList.contains("slot-letter")) {
      const rect = element.getBoundingClientRect();
      const position = Number(element.dataset.tileIndex) + (x > rect.left + rect.width / 2 ? 1 : 0);
      return pointerTargetFromPosition(element, Number(element.dataset.slotIndex), position, "tile");
    }

    const display = element.classList.contains("word-display")
      ? element
      : element.querySelector(".word-display");
    if (!display) return null;
    return pointerTargetFromPosition(element, Number(display.dataset.slotIndex), dropPositionFor(display, x), "display", display);
  }

  function pointerTargetFromPosition(element, slotIndex, position, kind, display = null) {
    if (!Number.isInteger(slotIndex) || !Number.isInteger(position)) return null;
    return { element, slotIndex, position, kind, display };
  }

  function dropPositionFor(display, x) {
    const letters = Array.from(display.querySelectorAll(".slot-letter"));
    for (let index = 0; index < letters.length; index += 1) {
      const rect = letters[index].getBoundingClientRect();
      if (x < rect.left + rect.width / 2) {
        return index;
      }
    }
    return letters.length;
  }

  function dropPositionFromTile(event, tile, tileIndex) {
    const rect = tile.getBoundingClientRect();
    return tileIndex + (event.clientX > rect.left + rect.width / 2 ? 1 : 0);
  }

  function markDisplayDropZone(display, x) {
    markInsertDropZone(display, dropPositionFor(display, x));
  }

  function markInsertDropZone(display, position) {
    if (!display) return;
    clearAllDropZones();
    const target = Array.from(display.querySelectorAll(".insert-target"))
      .find((item) => Number(item.dataset.position) === position);
    if (target && !target.disabled) target.classList.add("is-drop-target");
  }

  function clearDisplayDropZones(display) {
    if (!display) return;
    display.classList.remove("is-drop-target");
    display.querySelectorAll(".is-drop-target").forEach((item) => {
      item.classList.remove("is-drop-target");
    });
  }

  function clearAllDropZones() {
    document.querySelectorAll(".is-drop-target").forEach((item) => {
      item.classList.remove("is-drop-target");
    });
  }

  function scrollToTop() {
    const token = ++scrollResetToken;
    document.documentElement.classList.add("is-resetting-scroll");

    const resetScroll = () => {
      const target = view === "game" && !els.game.hidden ? els.game : els.appShell;
      target.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (document.scrollingElement) {
        document.scrollingElement.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.scrollingElement.scrollTop = 0;
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const releaseScroll = () => {
      if (token !== scrollResetToken) return;
      document.documentElement.classList.remove("is-resetting-scroll");
      resetScroll();
    };

    resetScroll();
    window.requestAnimationFrame(() => {
      resetScroll();
      window.requestAnimationFrame(releaseScroll);
    });
    window.setTimeout(releaseScroll, 250);
  }

  function activateInsertTarget(slotIndex, position) {
    if (state.submitted) return;
    if (state.selectedTileId) {
      moveSelectedTo(slotIndex, position);
      return;
    }
    placeLetter(slotIndex, position);
  }

  function placeLetter(slotIndex, position = state.slots[slotIndex].length) {
    if (!canEditGame() || state.cursor >= state.letters.length) return;
    const tile = {
      id: state.nextTileId,
      letter: state.letters[state.cursor]
    };
    state.nextTileId += 1;
    state.slots[slotIndex].splice(position, 0, tile);
    state.history.push({ slotIndex, tileId: tile.id });
    state.selectedTileId = null;
    state.cursor += 1;
    pulseTile(tile.id);
    saveDraftGame();
    if (state.cursor === 1) {
      gameActivity.record("started", state);
      void gameActivity.flush();
    }
    render();
  }

  function selectTile(tileId) {
    if (!canEditGame()) return;
    state.selectedTileId = state.selectedTileId === tileId ? null : tileId;
    render();
  }

  function setTileDragImage(event, tile) {
    if (!event.dataTransfer || !event.dataTransfer.setDragImage) return;

    const ghost = tile.cloneNode(true);
    ghost.classList.add("drag-image");
    ghost.style.position = "fixed";
    ghost.style.top = "-120px";
    ghost.style.left = "-120px";
    ghost.style.width = `${tile.offsetWidth}px`;
    document.body.appendChild(ghost);
    event.dataTransfer.setDragImage(ghost, tile.offsetWidth / 2, tile.offsetHeight / 2);
    window.setTimeout(() => ghost.remove(), 0);
  }

  function moveSelectedTo(targetSlotIndex, targetPosition) {
    if (!canEditGame()) return;
    const location = findTile(state.selectedTileId);
    if (!location || state.submitted) return;
    state.draggingTileId = null;
    const crossesSlot = location.slotIndex !== targetSlotIndex;
    if (crossesSlot && state.movesRemaining <= 0) {
      state.draggingTileId = null;
      showGameMessage("No swaps left!");
      render();
      return;
    }
    if (crossesSlot) {
      openSwapModal({
        tileId: state.selectedTileId,
        targetSlotIndex,
        targetPosition
      });
      return;
    }
    performSelectedMoveTo(targetSlotIndex, targetPosition);
  }

  function performSelectedMoveTo(targetSlotIndex, targetPosition) {
    if (!canEditGame()) return;
    const location = findTile(state.selectedTileId);
    if (!location || state.submitted) return;
    const crossesSlot = location.slotIndex !== targetSlotIndex;
    if (crossesSlot && state.movesRemaining <= 0) {
      showGameMessage("No swaps left!");
      render();
      return;
    }
    const [tile] = state.slots[location.slotIndex].splice(location.tileIndex, 1);
    let insertionIndex = targetPosition;
    if (location.slotIndex === targetSlotIndex && location.tileIndex < targetPosition) {
      insertionIndex -= 1;
    }
    insertionIndex = Math.max(0, Math.min(insertionIndex, state.slots[targetSlotIndex].length));
    state.slots[targetSlotIndex].splice(insertionIndex, 0, tile);
    if (crossesSlot) {
      state.movesRemaining -= 1;
      pulseMoves();
    }
    state.selectedTileId = null;
    state.draggingTileId = null;
    pulseTile(tile.id);
    saveDraftGame();
    render();
  }

  function openSwapModal(move) {
    pendingSwapMove = move;
    const remaining = state.movesRemaining;
    const totalMoves = DIFFICULTIES[state.difficultyKey].moves;
    els.swapTitleText.textContent = remaining === 1
      ? "use your only remaining row swap?"
      : `use 1 of your ${remaining} remaining row swaps?`;
    renderMoveDots(els.swapModalDots, totalMoves, totalMoves - remaining);
    els.swapModalDots.setAttribute("aria-label", `${remaining} row ${remaining === 1 ? "swap" : "swaps"} remaining`);
    setModalOpen(els.swapModal, true);
    els.confirmSwap.focus();
  }

  function closeSwapModal() {
    if (!isModalOpen(els.swapModal)) return;
    pendingSwapMove = null;
    setModalOpen(els.swapModal, false);
  }

  function confirmSwapMove() {
    const move = pendingSwapMove;
    if (!move) return;
    pendingSwapMove = null;
    setModalOpen(els.swapModal, false);
    state.selectedTileId = move.tileId;
    performSelectedMoveTo(move.targetSlotIndex, move.targetPosition);
  }

  function showGameMessage(message) {
    window.clearTimeout(gameMessageTimer);
    els.gameMessage.title = message;
    els.gameMessage.hidden = false;
    els.gameMessage.classList.remove("is-visible");
    window.requestAnimationFrame(() => els.gameMessage.classList.add("is-visible"));
    gameMessageTimer = window.setTimeout(hideGameMessage, 2200);
  }

  function hideGameMessage() {
    window.clearTimeout(gameMessageTimer);
    gameMessageTimer = null;
    els.gameMessage.classList.remove("is-visible");
    els.gameMessage.hidden = true;
  }

  function pulseTile(tileId) {
    state.activeTileId = tileId;
    window.setTimeout(() => {
      if (state.activeTileId === tileId) {
        state.activeTileId = null;
        const tile = els.board.querySelector(`.slot-letter[data-tile-id="${tileId}"]`);
        tile?.classList.remove("is-landing");
        tile?.closest(".slot-row")?.classList.remove("is-settling");
      }
    }, 360);
  }

  function pulseMoves() {
    state.moveBurst = true;
    window.setTimeout(() => {
      state.moveBurst = false;
      els.movesIndicator.classList.remove("moves-pop");
    }, 420);
  }

  function moveSelectedByOffset(offset) {
    const location = findTile(state.selectedTileId);
    if (!location || state.submitted) return;
    moveSelectedTo(location.slotIndex, location.tileIndex + offset + (offset > 0 ? 1 : 0));
  }

  function findTile(tileId) {
    if (!tileId) return null;
    for (let slotIndex = 0; slotIndex < state.slots.length; slotIndex += 1) {
      const tileIndex = state.slots[slotIndex].findIndex((tile) => tile.id === tileId);
      if (tileIndex !== -1) {
        return { slotIndex, tileIndex };
      }
    }
    return null;
  }

  function removeTile(tileId) {
    for (const slot of state.slots) {
      const tileIndex = slot.findIndex((tile) => tile.id === tileId);
      if (tileIndex !== -1) {
        slot.splice(tileIndex, 1);
        return;
      }
    }
  }

  function handleKeyboard(event) {
    if (event.key === "Escape" && isModalOpen(els.swapModal)) {
      closeSwapModal();
      return;
    }

    if (event.key === "Escape" && isModalOpen(els.settingsModal)) {
      closeSettingsModal();
      return;
    }

    if (event.key === "Escape" && isModalOpen(els.helpModal)) {
      closeHelpModal();
      return;
    }

    if (event.key === "Escape" && isModalOpen(els.shareModal)) {
      closeShareModal();
      return;
    }

    if (event.key === "Escape" && isModalOpen(els.dailyShareModal)) {
      setModalOpen(els.dailyShareModal, false);
      return;
    }

    if (event.key === "Escape" && isModalOpen(els.authModal)) {
      closeAuthModal();
      return;
    }

    if (view !== "game") return;
    gameEntrance.finish();

    const tag = event.target && event.target.tagName;
    if (tag === "TEXTAREA" || tag === "INPUT") return;

    if (event.key === "Escape") {
      state.selectedTileId = null;
      render();
      return;
    }

    if (event.key === "Enter" && tag !== "BUTTON" && state.cursor >= state.letters.length && !state.submitted) {
      event.preventDefault();
      submitGame();
      return;
    }

    if (event.key === "ArrowLeft" && state.selectedTileId && !state.submitted) {
      event.preventDefault();
      moveSelectedByOffset(-1);
      return;
    }

    if (event.key === "ArrowRight" && state.selectedTileId && !state.submitted) {
      event.preventDefault();
      moveSelectedByOffset(1);
      return;
    }

    if (/^[1-5]$/.test(event.key) && !state.submitted) {
      event.preventDefault();
      const slotIndex = Number(event.key) - 1;
      const position = event.shiftKey ? 0 : state.slots[slotIndex].length;
      if (state.selectedTileId) {
        moveSelectedTo(slotIndex, position);
      } else {
        placeLetter(slotIndex, position);
      }
    }
  }

  function canEditGame() {
    if (state.submitted || view !== "game") return false;
    if (isPlayableGameDate(state.date)) return true;
    updateViewHistory("home", "replace");
    enterHomeView();
    showGameMessage("This daily game has ended. Only today's games can be played.");
    return false;
  }

  function submitGame() {
    if (!canEditGame() || state.cursor < state.letters.length) return;
    const analyses = state.slots.map(analyseSlot);
    const targetScore = totalScore(analyses);
    const scoringSeed = state.seed;
    const scoringAttemptId = state.attemptId;
    if (!state.remoteCompletion) saveCompletedGame(analyses, targetScore);
    clearDraftGame(state.date, state.difficultyKey);
    if (!state.remoteCompletion) {
      void submitSignedInScore(analyses, targetScore);
    } else {
      showGameMessage("Completed on another device. This result is an unranked practice.");
    }
    state.submitted = true;
    gameActivity.record("ended", state);
    void gameActivity.flush();
    state.selectedTileId = null;
    state.draggingTileId = null;
    state.scoreDisplay = 0;
    state.revealedRows = 0;
    state.scoring = true;
    render();

    analyses.forEach((analysis, index) => {
      window.setTimeout(() => {
        if (
          state.seed !== scoringSeed
          || state.attemptId !== scoringAttemptId
          || !state.submitted
        ) return;
        state.revealedRows = Math.max(state.revealedRows, index + 1);
        render();
      }, ROW_REVEAL_START_MS + index * ROW_REVEAL_GAP_MS);
    });

    animateScore(targetScore, scoringSeed, scoringAttemptId);
  }

  function saveCompletedGame(analyses, score) {
    state.attemptId ||= createAttemptId();
    const history = historyForLocale();
    const dateEntries = history[state.date] || {};
    const validRows = analyses.filter((item) => item.valid).length;

    dateEntries[state.difficultyKey] = {
      locale: state.locale,
      date: state.date,
      difficulty: state.difficultyKey,
      puzzleVersion: state.puzzleVersion,
      poolWords: state.poolWords.slice(),
      letters: state.letters.slice(),
      rows: state.slots.map((slot) => slot.map((tile) => tile.letter)),
      score,
      scoringVersion: SCORING_VERSION,
      dictionaryVersion: dictionaryBundle.version,
      rowSwapsUsed: DIFFICULTIES[state.difficultyKey].moves - state.movesRemaining,
      rowSwapsLeft: state.movesRemaining,
      validRows,
      completedAt: new Date().toISOString(),
      attemptId: state.attemptId,
      syncStatus: currentUser ? "pending" : "local",
      syncError: null
    };
    history[state.date] = dateEntries;
    localStorage.setItem(historyStorageKey(), JSON.stringify(history));
  }

  function saveDraftGame() {
    if (state.submitted || state.remoteCompletion || !state.puzzleVersion) return;
    state.attemptId ||= createAttemptId();

    const drafts = draftsForLocale();
    const dateEntries = drafts[state.date] || {};
    dateEntries[state.difficultyKey] = {
      seed: state.seed,
      locale: state.locale,
      date: state.date,
      difficulty: state.difficultyKey,
      puzzleVersion: state.puzzleVersion,
      poolWords: state.poolWords.slice(),
      letters: state.letters.slice(),
      cursor: state.cursor,
      rows: state.slots.map((slot) => {
        return slot.map((tile) => ({
          id: tile.id,
          letter: tile.letter
        }));
      }),
      nextTileId: state.nextTileId,
      movesRemaining: state.movesRemaining,
      updatedAt: new Date().toISOString(),
      attemptId: state.attemptId
    };
    drafts[state.date] = dateEntries;
    localStorage.setItem(draftStorageKey(), JSON.stringify(drafts));
  }

  function clearDraftGame(date, difficultyKey) {
    const drafts = draftsForLocale();
    if (!drafts[date]?.[difficultyKey]) return;
    delete drafts[date][difficultyKey];
    if (Object.keys(drafts[date]).length === 0) delete drafts[date];
    localStorage.setItem(draftStorageKey(), JSON.stringify(drafts));
  }

  async function submitSignedInScore(analyses, score) {
    if (!currentUser) return;

    const payload = {
      locale: state.locale,
      date: state.date,
      difficulty: state.difficultyKey,
      puzzleVersion: state.puzzleVersion,
      finalRows: state.slots.map((slot) => slot.map((tile) => tile.letter)),
      rowSwapsUsed: DIFFICULTIES[state.difficultyKey].moves - state.movesRemaining,
      rowSwapsLeft: state.movesRemaining,
      claimedScore: score,
      completedAt: new Date().toISOString(),
      attemptId: state.attemptId
    };

    await withCompletionSyncLock(async () => {
      const entry = historyForLocale()[payload.date]?.[payload.difficulty];
      if (entry?.syncStatus === "synced") return;
      const outcome = await postScorePayload(payload);
      applySubmissionOutcome(entry || payloadToLocalEntry(payload), outcome);
    });
  }

  async function postScorePayload(payload, { refreshLeaderboard = true } = {}) {
    try {
      const response = await fetch("/api/scores", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload)
      });
      const responsePayload = await response.json().catch(() => ({}));
      if (response.ok) {
        if (refreshLeaderboard && payload.date === selectedDate) {
          loadLeaderboard(payload.date, { force: true, preserve: true });
        }
        return { status: "synced", completion: responsePayload.completion };
      }
      if (response.status === 409 && responsePayload.code === "already_completed") {
        return { status: "conflict", completion: responsePayload.completion };
      }
      if ([400, 404, 422].includes(response.status)) {
        return {
          status: "rejected",
          error: responsePayload.error || `Score submission was rejected (${response.status}).`
        };
      }
      return {
        status: "retry",
        error: responsePayload.error || `Score submission failed with status ${response.status}.`
      };
    } catch (error) {
      console.warn("Signed-in score submission failed.", error);
      return { status: "retry", error: error.message || "Network request failed." };
    }
  }

  function payloadToLocalEntry(payload) {
    return {
      locale: payload.locale,
      date: payload.date,
      difficulty: payload.difficulty,
      puzzleVersion: payload.puzzleVersion,
      rows: payload.finalRows,
      score: payload.claimedScore,
      rowSwapsUsed: payload.rowSwapsUsed,
      rowSwapsLeft: payload.rowSwapsLeft,
      attemptId: payload.attemptId,
      completedAt: payload.completedAt
    };
  }

  function scorePayloadForEntry(entry) {
    return {
      locale: entry.locale,
      date: entry.date,
      difficulty: entry.difficulty,
      puzzleVersion: entry.puzzleVersion,
      finalRows: entry.rows,
      rowSwapsUsed: entry.rowSwapsUsed,
      rowSwapsLeft: entry.rowSwapsLeft,
      claimedScore: entry.score,
      completedAt: entry.completedAt,
      attemptId: entry.attemptId
    };
  }

  function applySubmissionOutcome(entry, outcome) {
    if (["synced", "conflict"].includes(outcome.status) && outcome.completion) {
      mergeServerCompletions([outcome.completion]);
      return;
    }
    if (outcome.status !== "rejected") return;

    const history = historyForLocale();
    const stored = history[entry.date]?.[entry.difficulty];
    if (!stored || stored.attemptId !== entry.attemptId) return;
    stored.syncStatus = "rejected";
    stored.syncError = outcome.error || "Server validation rejected this result.";
    localStorage.setItem(historyStorageKey(), JSON.stringify(history));
    broadcastHistoryUpdate([]);
    render();
  }

  function mergeServerCompletions(completions) {
    if (!currentUser || !Array.isArray(completions) || !completions.length) return false;
    const history = historyForLocale();
    const drafts = draftsForLocale();
    const merged = [];
    let changed = false;

    completions.forEach((completion) => {
      if (completion.locale !== selectedLocale) return;
      const dateEntries = history[completion.date] || {};
      const local = dateEntries[completion.difficulty] || {};
      const challenge = dailyManifest?.date === completion.date
        && dailyManifest?.locale === completion.locale
        && dailyManifest?.puzzleVersion === completion.puzzleVersion
        ? dailyManifest.challenges?.[completion.difficulty]
        : null;
      const canonical = mergeCanonicalEntry(local, completion, {
        poolWords: challenge?.poolWords || challenge?.targetWords || [],
        letters: challenge?.letters || []
      });
      dateEntries[completion.difficulty] = canonical;
      history[completion.date] = dateEntries;
      merged.push(canonical);
      changed = true;

      if (drafts[completion.date]?.[completion.difficulty]) {
        delete drafts[completion.date][completion.difficulty];
        if (!Object.keys(drafts[completion.date]).length) delete drafts[completion.date];
      }
      applyRemoteCompletionToOpenGame(canonical);
    });

    if (!changed) return false;
    localStorage.setItem(historyStorageKey(), JSON.stringify(history));
    localStorage.setItem(draftStorageKey(), JSON.stringify(drafts));
    broadcastHistoryUpdate(merged);
    render();
    return true;
  }

  function applyRemoteCompletionToOpenGame(completion) {
    const action = remoteCompletionAction(view === "game" ? {
      date: state.date,
      difficulty: state.difficultyKey,
      submitted: state.submitted,
      attemptId: state.attemptId,
      cursor: state.cursor
    } : null, completion);
    if (action === "replace") {
      state = createCompletedGame(completion);
      return;
    }
    if (action !== "defer") return;
    state.remoteCompletion = completion;
    showGameMessage("Completed on another device. This board is now an unranked practice.");
  }

  function broadcastHistoryUpdate(completions) {
    syncChannel?.postMessage({
      type: "history-updated",
      userId: currentUser?.id || null,
      locale: selectedLocale,
      completions
    });
  }

  function handleSyncBroadcast(event) {
    const message = event.data;
    if (
      message?.type !== "history-updated"
      || !currentUser
      || message.userId !== currentUser.id
      || message.locale !== selectedLocale
    ) return;
    (message.completions || []).forEach(applyRemoteCompletionToOpenGame);
    render();
  }

  function handleCrossTabStorageChange(event) {
    if (![historyStorageKey(), draftStorageKey()].includes(event.key)) return;
    const completion = historyForLocale()[state.date]?.[state.difficultyKey];
    if (completion?.syncStatus === "synced") applyRemoteCompletionToOpenGame(completion);
    render();
  }

  async function syncSelectedDate({ force = false, reason = "selected-date" } = {}) {
    return syncCompletionRange(selectedDate, selectedDate, {
      force,
      reason,
      freshFor: SYNC_FRESH_MS
    });
  }

  async function syncCalendarMonth({ force = false, reason = "calendar" } = {}) {
    const from = `${calendarCursor}-01`;
    const end = new Date(`${calendarCursor}-01T00:00:00Z`);
    end.setUTCMonth(end.getUTCMonth() + 1);
    end.setUTCDate(0);
    const to = end.toISOString().slice(0, 10);
    return syncCompletionRange(from, to, {
      force,
      reason,
      freshFor: PAST_MONTH_FRESH_MS
    });
  }

  function syncCompletionRange(from, to, { force = false, freshFor = SYNC_FRESH_MS } = {}) {
    if (!currentUser) return Promise.resolve({ status: "anonymous" });
    const userId = currentUser.id;
    const locale = selectedLocale;
    const requestKey = `${userId}:${locale}:${from}:${to}`;
    const requestedAt = Date.now();
    if (syncRequests.has(requestKey)) return syncRequests.get(requestKey);

    const promise = withCompletionSyncLock(async () => {
      const freshnessKey = `${SYNC_STORAGE_PREFIX}fresh.${requestKey}`;
      const lastSuccess = Number(localStorage.getItem(freshnessKey) || 0);
      if (
        lastSuccess >= requestedAt
        || (!force && isSyncFresh(lastSuccess, Date.now(), freshFor))
      ) {
        return { status: "fresh" };
      }
      if (!currentUser || currentUser.id !== userId || selectedLocale !== locale) {
        return { status: "stale-session" };
      }

      try {
        const params = new URLSearchParams({ locale, from, to });
        const response = await fetch(`/api/scores?${params}`, { credentials: "include" });
        if (!response.ok) throw new Error(`Completion sync failed with status ${response.status}.`);
        const payload = await response.json();
        let changed = mergeServerCompletions(payload.scores || []);

        const history = historyForLocale();
        const pending = pendingCompletionEntries(history, { from, to });
        let uploadFailed = false;
        for (const entry of pending) {
          const outcome = await postScorePayload(scorePayloadForEntry(entry), { refreshLeaderboard: false });
          applySubmissionOutcome(entry, outcome);
          if (["synced", "conflict", "rejected"].includes(outcome.status)) changed = true;
          if (outcome.status === "retry") {
            uploadFailed = true;
            break;
          }
        }

        if (uploadFailed) {
          localStorage.removeItem(freshnessKey);
          return { status: "failed", changed };
        }
        localStorage.setItem(freshnessKey, String(Date.now()));
        if (changed && selectedDate >= from && selectedDate <= to) {
          loadLeaderboard(selectedDate, { force: true, preserve: true });
        }
        return { status: "synced", changed };
      } catch (error) {
        localStorage.removeItem(freshnessKey);
        console.warn("Completion sync failed.", error);
        return { status: "failed", error };
      } finally {
        render();
      }
    }).finally(() => syncRequests.delete(requestKey));
    syncRequests.set(requestKey, promise);
    return promise;
  }

  async function withCompletionSyncLock(callback) {
    if (!currentUser || !navigator.locks?.request) return callback();
    return navigator.locks.request(
      `totonoeyo-completion-sync:${currentUser.id}:${selectedLocale}`,
      callback
    );
  }

  async function beginInitialCompletionSync() {
    if (!currentUser) return;
    if (initialSyncStarted) return initialSyncPromise;
    initialSyncStarted = true;
    initialSyncPromise = (async () => {
      syncGatePending = true;
      render();
      const syncPromise = syncSelectedDate({ force: true, reason: "initial" });
      await settleWithin(syncPromise, SYNC_UI_DEADLINE_MS);
      syncGatePending = false;
      render();
      void syncPromise.finally(() => schedulePendingHistoryBackfill());
    })();
    return initialSyncPromise;
  }

  function schedulePendingHistoryBackfill() {
    if (!currentUser || pendingBackfillStarted) return;
    pendingBackfillStarted = true;
    window.setTimeout(() => void backfillPendingHistory(), 0);
  }

  async function backfillPendingHistory() {
    await withCompletionSyncLock(async () => {
      const entries = pendingCompletionEntries(historyForLocale());
      for (const entry of entries) {
        const outcome = await postScorePayload(scorePayloadForEntry(entry), { refreshLeaderboard: false });
        applySubmissionOutcome(entry, outcome);
        if (outcome.status === "retry") break;
      }
    });
    loadLeaderboard(selectedDate, { force: true, preserve: true });
  }

  let authResendTimer = null;
  let authResendAvailableAt = 0;

  function showAuthCodeStep(visible) {
    if (visible && !els.authEmail.value.trim()) {
      els.authMessage.textContent = "Enter your email address first.";
      els.authEmail.focus();
      return;
    }
    els.authCodeStep.hidden = !visible;
    els.authEnterCode.hidden = visible;
    els.authEmail.readonly = visible;
    els.authSubmit.textContent = visible ? "Resend email" : "Send sign-in email";
    els.authSubmit.setAttribute("kind", visible ? "ghost" : "primary");
    if (!visible) els.authMessage.textContent = "";
    (visible ? els.authCode : els.authEmail).focus();
    const input = els.authCode.shadowRoot?.querySelector("input");
    if (input) {
      input.inputMode = "numeric";
      input.maxLength = 11;
    }
  }

  function openAuthModal() {
    els.authMessage.textContent = "";
    els.authSubmit.disabled = Date.now() < authResendAvailableAt;
    els.authVerify.disabled = false;
    els.authCode.value = "";
    showAuthCodeStep(false);
    setModalOpen(els.authModal, true);
    els.authEmail.focus();
  }

  function closeAuthModal() {
    if (!isModalOpen(els.authModal)) return;
    setModalOpen(els.authModal, false);
  }

  async function requestMagicLink(event) {
    event.preventDefault();
    if (els.authSubmit.disabled) return;
    const email = els.authEmail.value.trim();
    if (!email) return;

    els.authSubmit.disabled = true;
    els.authMessage.textContent = "Sending sign-in email...";

    try {
      const csrf = await fetchAuthCsrf();
      const response = await fetch("/api/auth/signin/resend", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
          "x-auth-return-redirect": "1"
        },
        body: new URLSearchParams({
          email,
          csrfToken: csrf.csrfToken,
          callbackUrl: `${window.location.origin}${window.location.pathname}`
        })
      });

      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Sign-in is unavailable from this server.");
      const target = new URL(payload.url, window.location.origin);
      if (target.searchParams.has("error") || target.searchParams.get("auth")?.startsWith("error")) throw new Error("Sign-in is unavailable from this server.");
      showAuthCodeStep(true);
      els.authMessage.textContent = isLocalBrowser()
        ? "Sign-in link and code created. Open /api/dev/magic-links in this browser."
        : "Check your email. Use the link or enter the eight-digit code. Both expire in 10 minutes.";
      authResendAvailableAt = Date.now() + 60000;
      window.clearTimeout(authResendTimer);
      authResendTimer = window.setTimeout(() => { els.authSubmit.disabled = false; }, 60000);
    } catch (error) {
      els.authMessage.textContent = error.message;
      els.authSubmit.disabled = false;
    }
  }

  async function verifyLoginCode(event) {
    event.preventDefault();
    if (els.authVerify.disabled) return;
    const code = els.authCode.value.replace(/[\s-]/g, "");
    if (!/^\d{8}$/.test(code)) {
      els.authMessage.textContent = "Enter the eight-digit code from your email.";
      els.authCode.focus();
      return;
    }
    els.authVerify.disabled = true;
    els.authMessage.textContent = "Signing in...";
    try {
      const response = await fetch("/api/auth/code", {
        method: "POST", credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: els.authEmail.value.trim(), code, callbackUrl: window.location.pathname })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Sign-in is unavailable from this server.");
      const destination = new URL(payload.url, window.location.origin);
      if (destination.origin !== window.location.origin) throw new Error("Sign-in is unavailable from this server.");
      window.location.assign(destination.href);
    } catch (error) {
      els.authMessage.textContent = error.message;
      els.authVerify.disabled = false;
      els.authCode.focus();
    }
  }

  async function signOut() {
    try {
      const csrf = await fetchAuthCsrf();
      await fetch("/api/auth/signout", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
          "x-auth-return-redirect": "1"
        },
        body: new URLSearchParams({
          csrfToken: csrf.csrfToken,
          callbackUrl: `${window.location.origin}${window.location.pathname}`
        })
      });
    } finally {
      currentUser = null;
      initialSyncStarted = false;
      initialSyncPromise = null;
      pendingBackfillStarted = false;
      syncGatePending = false;
      accountState = { key: "", status: "idle", data: null };
      leaderboardState = { key: "", status: "idle", entries: [], currentUserEntry: null };
      if (view === "account") view = "home";
      render();
      loadLeaderboard(selectedDate);
      scrollToTop();
    }
  }

  async function fetchAuthCsrf() {
    const response = await fetch("/api/auth/csrf", { credentials: "include" });
    if (!response.ok) throw new Error("CSRF request failed.");
    return response.json();
  }

  function isLocalBrowser() {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "0.0.0.0" || hostname === "::1") return true;
    if (hostname.endsWith(".local")) return true;
    if (hostname.startsWith("10.") || hostname.startsWith("192.168.")) return true;

    const parts = hostname.split(".").map((part) => Number(part));
    if (parts.length !== 4 || parts.some((part) => !Number.isInteger(part) || part < 0 || part > 255)) return false;
    if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
    return parts[0] === 100 && parts[1] >= 64 && parts[1] <= 127;
  }

  function analyseSlot(tiles) {
    if (tiles.length === 0) {
      return {
        tiles,
        valid: false,
        word: "",
        score: 0,
        status: "Empty"
      };
    }

    const word = tiles.map((tile) => tile.letter).join("").toLowerCase();
    const found = validWords.has(word);
    const score = found ? scoreForLength(tiles.length) : 0;

    return {
      tiles,
      valid: found,
      word,
      score,
      status: found ? word.toUpperCase() : "No word"
    };
  }

  function totalScore(analyses) {
    const base = analyses.reduce((sum, item) => sum + item.score, 0);
    return base + (allPlacedLettersScore(analyses) ? CLEAN_BOARD_BONUS : 0);
  }

  function allPlacedLettersScore(analyses) {
    const usedRows = analyses.filter((item) => item.tiles.length > 0);
    return state.cursor === state.letters.length && usedRows.length > 0 && usedRows.every((item) => item.valid);
  }

  function animateScore(targetScore, scoringSeed, scoringAttemptId) {
    const startedAt = performance.now();
    const duration = SCORE_COUNT_DURATION_MS;

    function tick(now) {
      if (
        state.seed !== scoringSeed
        || state.attemptId !== scoringAttemptId
        || !state.submitted
      ) return;
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      state.scoreDisplay = Math.round(targetScore * eased);
      if (progress < 1) {
        render();
        requestAnimationFrame(tick);
        return;
      }
      state.scoreDisplay = targetScore;
      state.revealedRows = SLOT_COUNT;
      state.scoring = false;
      render();
    }

    requestAnimationFrame(tick);
  }

  function openShareModal() {
    if (!state.submitted) return;
    if (!currentUser) {
      renderShareGate();
      setModalOpen(els.shareModal, true);
      return;
    }
    renderShareModal();
    setModalOpen(els.shareModal, true);
  }

  function openDailyShareModal() {
    const scores = dailyShareSummary(currentUser, historyForLocale()[selectedDate]);
    if (!scores) return;
    const dateLabel = formatDisplayDate(selectedDate);
    els.dailyShareDate.textContent = dateLabel;
    for (const key of ["easy", "medium", "hard", "total"]) {
      document.getElementById(`dailyShareScore-${key}`).textContent = String(scores[key]);
    }
    els.dailyShareText.value = formatDailyShareText(dateLabel, scores, SHARE_URL);
    els.dailyShareCopy.textContent = "Copy text";
    setModalOpen(els.dailyShareModal, true);
  }

  function closeShareModal() {
    if (!isModalOpen(els.shareModal)) return;
    setModalOpen(els.shareModal, false);
  }

  async function openShareGallery() {
    if (!state.submitted || !currentUser || !state.attemptId) return;
    const gameKey = {
      date: state.date,
      difficulty: state.difficultyKey,
      attemptId: state.attemptId
    };
    els.shareGallery.disabled = true;
    els.shareGallery.textContent = "Preparing gallery...";

    const entry = historyForLocale()[gameKey.date]?.[gameKey.difficulty];
    if (entry?.syncStatus !== "synced") {
      const analyses = state.slots.map(analyseSlot);
      await submitSignedInScore(analyses, totalScore(analyses));
    }

    const stored = historyForLocale()[gameKey.date]?.[gameKey.difficulty];
    els.shareGallery.textContent = "Share gallery";
    if (stored?.syncStatus !== "synced" || stored.attemptId !== gameKey.attemptId) {
      els.shareGallery.disabled = false;
      showGameMessage("The gallery needs an internet connection to save this result first.");
      closeShareModal();
      return;
    }

    closeShareModal();
    showGallery(stored.attemptId);
  }

  function openHelpModal() {
    setModalOpen(els.helpModal, true);
  }

  function closeHelpModal() {
    if (!isModalOpen(els.helpModal)) return;
    setModalOpen(els.helpModal, false);
  }

  function openSettingsModal() {
    els.localeSelect.value = selectedLocale;
    els.publicNameMessage.textContent = "";
    els.productEmailsMessage.textContent = "";
    renderPublicNameSettings();
    renderProductEmailSettings();
    setModalOpen(els.settingsModal, true);
  }

  function closeSettingsModal() {
    if (!isModalOpen(els.settingsModal)) return;
    setModalOpen(els.settingsModal, false);
  }

  function setModalOpen(modal, open) {
    modal.open = open;
  }

  function isModalOpen(modal) {
    return Boolean(modal.open);
  }

  function renderPublicNameSettings() {
    els.publicNameSettings.hidden = !currentUser;
    if (!currentUser) return;

    const remaining = Number(currentUser.rerollsRemaining || 0);
    els.publicNameValue.textContent = currentUser.publicName || currentUser.displayName;
    els.rerollPublicName.textContent = remaining > 0 ? `Reroll · ${remaining} left` : "No rerolls left";
    els.rerollPublicName.disabled = remaining <= 0;
  }

  function renderProductEmailSettings() {
    els.productEmailSettings.hidden = !currentUser;
    if (!currentUser) return;
    els.productEmailsCheckbox.checked = Boolean(currentUser.productEmailsEnabled);
    els.productEmailsCheckbox.disabled = false;
  }

  async function updateProductEmailPreference() {
    if (!currentUser) return;
    const previousValue = Boolean(currentUser.productEmailsEnabled);
    const productEmailsEnabled = els.productEmailsCheckbox.checked;
    els.productEmailsCheckbox.disabled = true;
    els.productEmailsMessage.textContent = "Saving...";

    try {
      const response = await fetch("/api/account/preferences", {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ productEmailsEnabled })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Could not save email preference.");

      currentUser.productEmailsEnabled = Boolean(payload.preferences.productEmailsEnabled);
      if (accountState.data) accountState.data.preferences = payload.preferences;
      els.productEmailsMessage.textContent = productEmailsEnabled
        ? "Product emails enabled."
        : "Product emails disabled.";
    } catch (error) {
      els.productEmailsCheckbox.checked = previousValue;
      els.productEmailsMessage.textContent = error.message || "Could not save email preference.";
    } finally {
      els.productEmailsCheckbox.disabled = false;
    }
  }

  async function rerollPublicName() {
    if (!currentUser) return;
    els.rerollPublicName.disabled = true;
    els.publicNameMessage.textContent = "Choosing another name...";

    try {
      const response = await fetch("/api/me/public-name/reroll", {
        method: "POST",
        credentials: "include"
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Could not change leaderboard name.");

      currentUser.publicName = payload.profile.publicName;
      currentUser.displayName = payload.profile.publicName;
      currentUser.rerollsRemaining = payload.profile.rerollsRemaining;
      if (accountState.data?.account) {
        accountState.data.account.publicName = payload.profile.publicName;
        accountState.data.account.displayName = payload.profile.publicName;
      }
      loadLeaderboard(selectedDate, { force: true, preserve: true });
      els.publicNameMessage.textContent = "Leaderboard name updated.";
      render();
      renderPublicNameSettings();
    } catch (error) {
      els.publicNameMessage.textContent = error.message || "Could not change leaderboard name.";
      renderPublicNameSettings();
    }
  }

  function renderShareModal() {
    const analyses = state.slots.map(analyseSlot);
    const validRows = analyses.filter((item) => item.valid).length;

    els.shareScore.textContent = String(totalScore(analyses));
    els.shareMoves.textContent = String(state.movesRemaining);
    els.shareValidRows.textContent = `${validRows}/${SLOT_COUNT}`;
    els.sharePoolWords.classList.remove("is-message");
    renderPoolWordItems(els.sharePoolWords, state.poolWords);
    els.shareText.value = buildShareText();
    els.copy.disabled = false;
    els.shareGallery.disabled = !state.attemptId;
    els.shareGallery.textContent = "Share gallery";
  }

  function renderShareGate() {
    const analyses = state.slots.map(analyseSlot);
    const validRows = analyses.filter((item) => item.valid).length;

    els.shareScore.textContent = String(totalScore(analyses));
    els.shareMoves.textContent = String(state.movesRemaining);
    els.shareValidRows.textContent = `${validRows}/${SLOT_COUNT}`;
    els.sharePoolWords.classList.add("is-message");
    els.sharePoolWords.textContent = "Sign in to share results and submit to leaderboards.";
    els.shareText.value = "Sharing requires a signed-in account. Your completed game has been saved locally on this device.";
    els.copy.disabled = true;
    els.shareGallery.disabled = true;
    els.shareGallery.textContent = "Share gallery";
  }

  function buildShareText() {
    const analyses = state.slots.map(analyseSlot);
    const baseScore = analyses.reduce((sum, item) => sum + item.score, 0);
    const bonus = allPlacedLettersScore(analyses) ? CLEAN_BOARD_BONUS : 0;
    const score = baseScore + bonus;
    const rows = analyses.map(formatShareRow);
    const scoreLine = bonus > 0
      ? `Score: ${baseScore} + ${bonus} = ${score}`
      : `Score: ${score}`;

    return [
      "totonoeyo ととのえよう toh-toh-no-eh-yo",
      `${formatDisplayDate(state.date)} · ${DIFFICULTIES[state.difficultyKey].label}`,
      "",
      ...rows,
      "",
      scoreLine,
      `Row swaps left: ${redactedMoves()}`,
      "",
      SHARE_URL
    ].join("\n");
  }

  function formatDisplayDate(dateKey) {
    const date = new Date(`${dateKey}T00:00:00Z`);
    const day = date.getUTCDate();
    const month = date.toLocaleString("en-GB", { month: "long", timeZone: "UTC" });
    const year = date.getUTCFullYear();
    return `${day}${ordinalSuffix(day)} ${month} ${year}`;
  }

  function todayKey() {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: DAILY_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  }

  function monthKey(dateKey) {
    return String(dateKey || todayKey()).slice(0, 7);
  }

  function shiftMonth(month, offset) {
    const date = new Date(`${month}-01T00:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() + offset);
    return date.toISOString().slice(0, 7);
  }

  function shiftDate(dateKey, amount) {
    const date = new Date(`${dateKey}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + amount);
    return date.toISOString().slice(0, 10);
  }

  function formatMonthLabel(month) {
    const date = new Date(`${month}-01T00:00:00Z`);
    return date.toLocaleString("en-GB", {
      month: "long",
      year: "numeric",
      timeZone: "UTC"
    });
  }

  function historyStorageKey() {
    return currentUser
      ? `${HISTORY_STORAGE_PREFIX}${currentUser.id}.${selectedLocale}`
      : `${LEGACY_HISTORY_STORAGE_PREFIX}${selectedLocale}`;
  }

  function draftStorageKey() {
    return currentUser
      ? `${DRAFT_STORAGE_PREFIX}${currentUser.id}.${selectedLocale}`
      : `${LEGACY_DRAFT_STORAGE_PREFIX}${selectedLocale}`;
  }

  function historyForLocale() {
    const key = historyStorageKey();
    const history = readStoredObject(key);
    if (!currentUser) return history;

    let changed = false;
    Object.values(history).forEach((dateEntries) => {
      Object.values(dateEntries || {}).forEach((entry) => {
        if (!entry.attemptId) {
          entry.attemptId = createAttemptId();
          changed = true;
        }
        if (!["pending", "synced", "rejected"].includes(entry.syncStatus)) {
          entry.syncStatus = "pending";
          changed = true;
        }
      });
    });
    if (changed) localStorage.setItem(key, JSON.stringify(history));
    return history;
  }

  function draftsForLocale() {
    return readStoredObject(draftStorageKey());
  }

  function readStoredObject(storageKey) {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "{}");
    } catch (error) {
      return {};
    }
  }

  function migrateLegacyStorageForCurrentUser() {
    if (!currentUser) return;
    const migrationKey = `${SYNC_STORAGE_PREFIX}migrated.${currentUser.id}.${selectedLocale}`;
    if (localStorage.getItem(migrationKey)) return;

    const scopedHistoryKey = historyStorageKey();
    const scopedDraftKey = draftStorageKey();
    const legacyOwnerKey = `${SYNC_STORAGE_PREFIX}legacy-owner.${selectedLocale}`;
    const legacyOwner = localStorage.getItem(legacyOwnerKey);
    const canClaimLegacy = !legacyOwner || legacyOwner === currentUser.id;
    if (canClaimLegacy && !localStorage.getItem(scopedHistoryKey)) {
      const legacyHistory = localStorage.getItem(`${LEGACY_HISTORY_STORAGE_PREFIX}${selectedLocale}`);
      if (legacyHistory) localStorage.setItem(scopedHistoryKey, legacyHistory);
    }
    if (canClaimLegacy && !localStorage.getItem(scopedDraftKey)) {
      const legacyDrafts = localStorage.getItem(`${LEGACY_DRAFT_STORAGE_PREFIX}${selectedLocale}`);
      if (legacyDrafts) localStorage.setItem(scopedDraftKey, legacyDrafts);
    }
    if (canClaimLegacy) localStorage.setItem(legacyOwnerKey, currentUser.id);
    localStorage.setItem(migrationKey, new Date().toISOString());
    historyForLocale();
  }

  function createAttemptId() {
    if (crypto.randomUUID) return crypto.randomUUID();
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (character) => (
      (Number(character) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(character) / 4).toString(16)
    ));
  }

  function ordinalSuffix(day) {
    if (day % 100 >= 11 && day % 100 <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  function redactedMoves() {
    const totalMoves = DIFFICULTIES[state.difficultyKey].moves;
    const usedMoves = totalMoves - state.movesRemaining;
    return `${"⚪".repeat(usedMoves)}${"⚫".repeat(state.movesRemaining)}`;
  }

  function pick(items, rng) {
    return items[Math.floor(rng() * items.length)];
  }

  function pickUnused(items, rng, used) {
    const available = items.filter((item) => !used.has(item));
    const word = pick(available.length ? available : items, rng);
    used.add(word);
    return word;
  }

  function shuffle(items, rng) {
    const copy = items.slice();
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(rng() * (index + 1));
      const value = copy[index];
      copy[index] = copy[swapIndex];
      copy[swapIndex] = value;
    }
    return copy;
  }

  function mulberry32(seed) {
    return function () {
      let value = seed += 0x6d2b79f5;
      value = Math.imul(value ^ value >>> 15, value | 1);
      value ^= value + Math.imul(value ^ value >>> 7, value | 61);
      return ((value ^ value >>> 14) >>> 0) / 4294967296;
    };
  }

  function hashString(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  function parseAppRoute(pathname) {
    const gameMatch = pathname.match(/^\/game\/(\d{4}-\d{2}-\d{2})\/(easy|medium|hard)\/?$/);
    if (gameMatch) {
      return { view: "game", date: gameMatch[1], difficulty: gameMatch[2] };
    }
    const leaderboardMatch = pathname.match(/^\/leaderboard\/(\d{4}-\d{2}-\d{2})\/?$/);
    if (leaderboardMatch) return { view: "leaderboard", date: leaderboardMatch[1] };
    const galleryMatch = pathname.match(/^\/gallery\/([0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\/?$/i);
    if (galleryMatch) return { view: "gallery", completionId: galleryMatch[1].toLowerCase() };
    if (/^\/account\/?$/.test(pathname)) return { view: "account" };
    const infoMatch = pathname.match(/^\/(privacy|terms|contact|about)\/?$/);
    if (infoMatch) return { view: "info", page: infoMatch[1] };
    return { view: "home" };
  }

  function routePath(route) {
    if (route.view === "game" && route.date && route.difficulty) {
      return `/game/${route.date}/${route.difficulty}`;
    }
    if (route.view === "leaderboard" && route.date) return `/leaderboard/${route.date}`;
    if (route.view === "gallery" && route.completionId) return `/gallery/${route.completionId}`;
    if (route.view === "account") return "/account";
    if (route.view === "info" && route.page) return `/${route.page}`;
    return "/";
  }

  async function initializeApp() {
    if (
      Array.isArray(dictionaryBundle.locales?.[selectedLocale])
      && (dictionaryBundle.schemaVersion !== 2 || Array.isArray(dictionaryBundle.common))
    ) {
      prepareBundledDictionary(selectedLocale);
    }
    render();
    const currentUserPromise = loadCurrentUser();
    await Promise.all([
      settleWithin(currentUserPromise, SYNC_UI_DEADLINE_MS),
      loadDailyManifest(),
      loadSiteBanner()
    ]);
    if (currentUser) await beginInitialCompletionSync();
    void loadLeaderboard(selectedDate);

    if (initialRoute.view === "game") {
      if (await openGame(initialRoute.difficulty, "replace")) return;
      updateViewHistory("home", "replace");
      enterHomeView();
      return;
    }

    if (initialRoute.view === "account") {
      if (currentUser) {
        updateViewHistory("account", "replace");
        render();
        scrollToTop();
        loadAccountDetails();
        return;
      }
    }

    if (initialRoute.view === "leaderboard") {
      showLeaderboard(initialRoute.date, "replace");
      return;
    }

    if (initialRoute.view === "gallery") {
      showGallery(initialRoute.completionId, "replace");
      return;
    }

    if (initialRoute.view === "info") {
      showInfoPage(initialRoute.page, "replace");
      return;
    }

    if (initialRoute.view !== "home") updateViewHistory("home", "replace");
    enterHomeView();
  }

  async function revealApp() {
    const componentsReady = ["cds-tile", "cds-icon-button", "cds-overflow-menu", "cds-accordion"]
      .map((name) => customElements.whenDefined(name));
    if (document.fonts?.ready) componentsReady.push(document.fonts.ready);
    await Promise.allSettled(componentsReady);
    els.appShell.hidden = false;
    els.appShell.setAttribute("aria-busy", "false");
  }

  initializeApp()
    .catch((error) => console.error("Application initialization failed.", error))
    .finally(revealApp);
}());
